import React, { FC, ReactElement, useState, useRef, useEffect, useMemo, useLayoutEffect } from "react";
import {
  Box,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Typography as MuiTypography,
  useMediaQuery,
  Drawer,
} from "@mui/material";
import { FiInfo, FiDownload, FiMenu, FiPlus } from "react-icons/fi";
import ChatBubble from "./ChatBubble";
import InlineDutyCalculator, { InlineDutyCalculatorState } from "./InlineDutyCalculator";
import GenerativeUIBlock, { UIChoiceSpec } from "./GenerativeUIBlock";
// ThinkingDisplay has been replaced with AIProgressIndicator for consistent UI
import MessageActions from "./MessageActions";
import AIProgressIndicator, { AIProgressState } from "./AIProgressIndicator";
import { useChatPdfExport } from "./useChatPdfExport";
import { trackChatOpened, trackExportDownloaded } from "@/analytics/posthog-events";
import {
  useCreateChatMutation,
  useGetChatsQuery,
  useGetChatQuery,
} from "@/services/chat";
import {
  useGetChatAttachmentsQuery,
  useLazyGenerateChatAttachmentAccessSignedUrlQuery,
} from "@/services/chat/chat-attachment-api";
import {
  CREATE_AGENT_THREAD_MUTATION,
  SEND_AGENT_MESSAGE_MUTATION,
} from "@/graphql/chat-requests";
import { request } from "graphql-request";
import { gqlServerURL } from "@/config";
import { useAgentChatStream, type AgentStreamEvent, type StreamHandlers } from "./useAgentChatStream";
import ChatErrorBoundary from "./ChatErrorBoundary";
import { Chat, ChatMessage, UIChatMessage, UIChatSession, MessageSender, ChatAttachment } from "@/types/chat";
import Sidebar from "./Sidebar";
import NewChatView from "./NewChatView";
import ChatInputArea from "./ChatInputArea";
import { useAppSelector } from "@/store";
import useSnackbar from "@/hooks/useSnackbar";
import useChatAttachmentUpload from "@/hooks/useChatAttachmentUpload";
import { tokens } from "@/tokens";

// Lightweight, structured logging focused on ordering and streaming state
// Disabled in production to prevent console noise and re-render debugging only when needed
const DEBUG_STREAMING = false; // Set to true to enable verbose streaming logs
function logOrder(label: string, payload?: any) {
  if (!DEBUG_STREAMING) return;
  try {
    // eslint-disable-next-line no-console
    console.log('[Order]', label, payload ?? '');
  } catch {}
}

const convertApiChatToUI = (apiChat: Chat): UIChatSession => ({
  id: apiChat._id,
  title: apiChat.title,
  timestamp: apiChat.lastActivityAt || new Date().toISOString(),
  lastMessage: apiChat.messages?.length ? apiChat.messages[apiChat.messages.length - 1].content : "",
  messages: apiChat.messages?.map(convertApiMessageToUI) || [],
});

const convertToolName = (toolName: string): string => {
  // Map technical tool names to user-friendly display names
  const toolNameMap: Record<string, string> = {
    'get_hts_hierarchy': 'HTS Database',
    'get_cbp_rulings_for_hts': 'CROSS Rulings',
    'get_cbp_rulings_for_heading6': 'CROSS Rulings',
    'get_cbp_associated_hts': 'CROSS Rulings',
    'get_cbp_rulings_for_heading': 'CROSS Rulings',
    'search_cbp_rulings': 'CROSS Rulings Search',
    'search_mcp_resources': 'Resource Search',
    'fetch_mcp_resource': 'Resource Fetch',
    'list_mcp_resources': 'Resource List',
  };
  
  return toolNameMap[toolName] || toolName;
};

const convertApiMessageToUI = (apiMessage: ChatMessage): UIChatMessage => {
  return {
    id: apiMessage._id,
    content: apiMessage.content,
    sender: apiMessage.source,
    timestamp: apiMessage.createdAt || new Date().toISOString(),
    llmMetadata: apiMessage.llmMetadata,
    finalizeArgs: apiMessage.finalizeArgs,
    // Preserve messageOrder for sorting
    messageOrder: (apiMessage as any).messageOrder,
    attachmentIds: (apiMessage as any).attachmentIds,
  };
};

const ChatAdvisor: FC = (): ReactElement => {
  const [currentMessage, setCurrentMessage] = useState("");
  const [showChatInterface, setShowChatInterface] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [optimisticMessages, setOptimisticMessages] = useState<UIChatMessage[]>([]);
  const [showAIThinking, setShowAIThinking] = useState(false);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const [liveStreamText, setLiveStreamText] = useState<string>("");
  // Populated when the agent emits its `finalize` event mid-stream. The
  // live virtual message renders from here so the analysis markdown,
  // clarifying-question block, and HTS code surface immediately when
  // finalize lands — before the persist refetch completes.
  const [liveFinalizeArgs, setLiveFinalizeArgs] = useState<import("@/types/chat").FinalizeArgs | null>(null);
  const [liveThinkingList, setLiveThinkingList] = useState<string[]>([]);
  const [currentToolCalls, setCurrentToolCalls] = useState<{ name: string; args: any }[]>([]);
  const [activeToolCalls, setActiveToolCalls] = useState<Set<string>>(new Set());
  const [dutyCalculatorStates, setDutyCalculatorStates] = useState<Record<string, InlineDutyCalculatorState>>({});
  const [aiProgressState, setAiProgressState] = useState<AIProgressState>("idle");
  const [aiProgressStatus, setAiProgressStatus] = useState<string>("");
  // Mirror for awaitingPersistAiRef to drive effects reliably
  const [awaitingPersist, setAwaitingPersist] = useState<boolean>(false);
  const [streamingCode, setStreamingCode] = useState<string | null>(null);
  const autoCreatedRef = useRef<boolean>(false);
  const createInFlightRef = useRef<Promise<string | null> | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const topOverlayRef = useRef<HTMLDivElement>(null);
  const bottomOverlayRef = useRef<HTMLDivElement>(null);
  const [overlayTopPx, setOverlayTopPx] = useState<number>(0);
  const [overlayBottomPx, setOverlayBottomPx] = useState<number>(0);

  const { exportChatToPdf } = useChatPdfExport();
  const [isExporting, setIsExporting] = useState(false);

  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const { user } = useAppSelector((state) => state.auth);
  const accessToken = useAppSelector((state) => (state as any)?.auth?.accessToken);
  const { show: showSnackbar } = useSnackbar();
  
  // Chat attachments
  const {
    pendingAttachments,
    uploadAttachment,
    associateAttachmentsWithChat,
    removeAttachment,
    clearAttachments,
    isUploading: isUploadingAttachment,
    hasPendingAttachments,
    getAttachmentIds,
    getPendingAttachmentIds,
  } = useChatAttachmentUpload();

  // Persisted attachments for the selected chat (for rendering in thread)
  const { data: chatAttachments = [] } = useGetChatAttachmentsQuery(selectedChatId || "", {
    skip: !selectedChatId,
  });
  const [getAccessUrl] = useLazyGenerateChatAttachmentAccessSignedUrlQuery();
  const [attachmentUrlMap, setAttachmentUrlMap] = useState<Record<string, string>>({});
  const blobPreviewUrlsRef = useRef<Record<string, string>>({});

  const revokeBlobUrlIfNeeded = (url?: string) => {
    try {
      if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
    } catch {}
  };
  const attachmentsById = useMemo(() => {
    const map: Record<string, ChatAttachment> = {};
    for (const a of chatAttachments) map[a._id] = a;
    return map;
  }, [chatAttachments]);

  // Ensure we have signed URLs for image attachments so we can preview them in the thread
  useEffect(() => {
    if (!selectedChatId) return;
    if (!chatAttachments || chatAttachments.length === 0) return;

    chatAttachments.forEach((att) => {
      if (!att.mimeType?.startsWith("image/")) return;
      const existingUrl = attachmentUrlMap[att._id];
      if (existingUrl && !existingUrl.startsWith("blob:")) return;

      getAccessUrl({ id: att._id })
        .unwrap()
        .then((res) => {
          setAttachmentUrlMap((prev) => {
            const prevUrl = prev[att._id];
            if (prevUrl && prevUrl.startsWith("blob:")) {
              revokeBlobUrlIfNeeded(prevUrl);
              try { delete blobPreviewUrlsRef.current[att._id]; } catch {}
            }
            return { ...prev, [att._id]: res.signedUrl };
          });
        })
        .catch(() => {
          // ignore; UI will show filename only
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChatId, chatAttachments]);

  // Revoke any blob preview URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      try {
        Object.values(blobPreviewUrlsRef.current || {}).forEach((u) => revokeBlobUrlIfNeeded(u));
        blobPreviewUrlsRef.current = {};
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cramped mode when viewport is effectively small (e.g., high zoom)
  const isShort = useMediaQuery('(max-height:750px)');
  const isNarrow = useMediaQuery('(max-width:900px)');
  const isVeryShort = useMediaQuery('(max-height:640px)');
  const isMobileWidth = useMediaQuery('(max-width:600px)');
  const isCramped = isShort || isNarrow; // Use drawer sidebar for cramped views
  const isSuperCramped = isVeryShort || isMobileWidth; // hide subtitle & suggestions threshold
  // Use drawer for cramped/mobile views instead of inline sidebar
  const useDrawerSidebar = isCramped;
  // Collapsible chat sidebar for non-cramped view (desktop only)
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true);
  // Drawer state for cramped/mobile views
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState<boolean>(false);
  useEffect(() => {
    // On desktop (not cramped), sidebar is always open
    setIsSidebarOpen(!isCramped);
  }, [isCramped]);
  const sidebarWidth = 'clamp(240px, 24vw, 360px)'; // Desktop only now

  // Clear streaming state on initial mount/chat change to prevent stale state after refresh
  useEffect(() => {
    if (selectedChatId) {
      // Clear any retry intervals
      if ((window as any).__chatStreamRetryInterval) {
        clearInterval((window as any).__chatStreamRetryInterval);
        (window as any).__chatStreamRetryInterval = null;
      }

      // Dispose any active WebSocket subscription to prevent content leakage between chats
      if (wsSubDisposeRef.current) {
        try { wsSubDisposeRef.current(); } catch {}
        wsSubDisposeRef.current = null;
      }

      // Clear any lingering streaming state when switching chats or on initial load
      setLiveStreamText(""); setLiveFinalizeArgs(null);
      setLiveThinkingList([]);
      setCurrentToolCalls([]);
      setActiveToolCalls(new Set());
      setShowAIThinking(false);
      awaitingPersistAiRef.current = false;
      setAwaitingPersist(false);
      streamingMessageOrderRef.current = null;
      highestPersistedMessageOrderRef.current = null; // Reset on chat change
    }
  }, [selectedChatId]);

  // Clear per-chat attachment preview URLs when switching chats (and revoke blob previews)
  useEffect(() => {
    try {
      Object.values(blobPreviewUrlsRef.current || {}).forEach((u) =>
        revokeBlobUrlIfNeeded(u)
      );
      blobPreviewUrlsRef.current = {};
    } catch {}
    setAttachmentUrlMap({});
  }, [selectedChatId]);

  const chatFilter = useMemo(
    () =>
      selectedClient
        ? { clientId: selectedClient._id, pageNo: 1, pageSize: 50 }
        : { pageNo: 1, pageSize: 50 },
    [selectedClient]
  );
  const { data: chatsData, isLoading: chatsLoading, error: chatsError, refetch: refetchChats } = useGetChatsQuery(chatFilter, {
    refetchOnMountOrArgChange: true,
  });
  const { data: selectedChatData, refetch: refetchSelectedChat } = useGetChatQuery(selectedChatId || "", {
    skip: !selectedChatId,
    refetchOnMountOrArgChange: true,
  });

  // The agent backend auto-renames a thread AFTER it emits `done` (title
  // generation runs against the just-persisted exchange). A single refetch
  // on `done` races and almost always returns the placeholder title; poll
  // a few times with backoff until the title changes, then refresh the
  // selected-chat header so the new title appears in the open view too.
  const pollForRenamedTitle = (chatId: string, originalTitle: string) => {
    // Title gen on Gemini 3.1 Pro (a thinking model) can take 5–10s, so the
    // polling window has to cover that. Event-driven refresh via the
    // `thread_renamed` stream event is the fast path; this is the fallback.
    const MAX_ATTEMPTS = 10;
    const INTERVAL_MS = 1200;
    const INITIAL_DELAY_MS = 500;
    void (async () => {
      await new Promise((r) => setTimeout(r, INITIAL_DELAY_MS));
      for (let i = 0; i < MAX_ATTEMPTS; i++) {
        try {
          const res: any = await refetchChats?.();
          const list = res?.data?.data as any[] | undefined;
          const updated = Array.isArray(list)
            ? list.find((c: any) => c._id === chatId)
            : null;
          if (updated && updated.title && updated.title !== originalTitle) {
            try { refetchSelectedChat && refetchSelectedChat(); } catch {}
            return;
          }
        } catch {}
        if (i < MAX_ATTEMPTS - 1) await new Promise((r) => setTimeout(r, INTERVAL_MS));
      }
    })();
  };
  
  // Build a stable StreamHandlers object that maps agent stream events
  // onto the chat advisor's state setters. The same handler set is used
  // by sendStreamingMessage (new outbound message) and by the auto-
  // rejoin effect (catching up an in-flight stream after a refresh /
  // re-entry). The event-mapping body is intentionally identical to
  // the inline handler that used to live in sendStreamingMessage — the
  // refactor moved it here so rejoin and send share one source of
  // truth, but the per-event behavior is unchanged.
  //
  // We close over the streamId so the deferred-dispose-after-`done`
  // check can compare it against currentStreamIdRef and avoid tearing
  // down a brand-new subscription if the user sent another message in
  // the 15s grace window.
  const buildAgentStreamHandlers = (
    chatId: string,
    titleAtSend: string,
    streamId: string
  ): StreamHandlers => ({
    onEvent: (ev: AgentStreamEvent, source: "replay" | "live") => {
      const metadata = (ev.metadata || {}) as Record<string, unknown>;
      logOrder("StreamEvent", { type: ev.type, order: (metadata as { messageOrder?: number }).messageOrder });
      // Bump the watchdog timestamp on every event so a healthy stream
      // never trips the no-event recovery path.
      lastEventTsRef.current = Date.now();

      if (ev.type === "tool_call") {
        isStreamingThoughtRef.current = false;
        const toolName = convertToolName(((metadata as { name?: string }).name) || "Unknown tool");
        const args = ((metadata as { args?: Record<string, unknown> }).args) || {};
        logOrder("AgentToolCall", { toolName, args });
        setAiProgressState("using_tools");
        setAiProgressStatus(`Using ${toolName}...`);
        setCurrentToolCalls((prev) => {
          const exists = prev.some((tc) => tc.name === toolName);
          if (exists) return prev;
          return [...prev, { name: toolName, args }];
        });
        setActiveToolCalls((prev) => {
          if (prev.has(toolName)) return prev;
          return new Set([...prev, toolName]);
        });
      }
      if (ev.type === "tool_result") {
        setAiProgressState("thinking");
        setAiProgressStatus("Analyzing...");
      }
      if (ev.type === "thinking_delta") {
        const token = String(ev.content || "");
        if (!token) return;
        setAiProgressState("thinking");
        setAiProgressStatus("Reasoning...");
        if (isStreamingThoughtRef.current) {
          setLiveThinkingList((prev) => {
            if (prev.length === 0) return [token];
            const copy = prev.slice();
            copy[copy.length - 1] = (copy[copy.length - 1] || "") + token;
            return copy;
          });
        } else {
          setLiveThinkingList((prev) => [...prev, token]);
          isStreamingThoughtRef.current = true;
        }
      }
      if (ev.type === "text_delta") {
        isStreamingThoughtRef.current = false;
        const token = String(ev.content || "");
        setActiveToolCalls(new Set());
        setAiProgressState("generating");
        setAiProgressStatus("Generating response...");
        setLiveStreamText((prev) => (prev || "") + token);
      }
      if (ev.type === "finalize") {
        // Structured answer envelope from the agent's finalize tool.
        // The backend smuggles `args` through `metadata`. Drive the
        // live render off this so the analysis markdown and any
        // clarifying-question block / HTS code surface immediately
        // without waiting for the persist refetch.
        //
        // Skip on replay: the persisted message already carries
        // finalizeArgs and is rendered in the message list. Writing
        // them to live state too would render the answer twice while
        // we wait for the 6s backstop to clear.
        if (source === "replay") return;
        isStreamingThoughtRef.current = false;
        const args = (ev.metadata as { args?: import("@/types/chat").FinalizeArgs } | null | undefined)?.args;
        if (args) {
          setLiveFinalizeArgs(args);
          setLiveStreamText(args.analysis_markdown || "");
        }
        setActiveToolCalls(new Set());
        setAiProgressState("generating");
        setAiProgressStatus("Generating response...");
      }
      if (ev.type === "thread_renamed") {
        try { refetchChats && refetchChats(); } catch {}
        try { refetchSelectedChat && refetchSelectedChat(); } catch {}
        try { wsSubDisposeRef.current?.(); } catch {}
        wsSubDisposeRef.current = null;
        currentStreamIdRef.current = null;
      }
      if (ev.type === "done") {
        isStreamingThoughtRef.current = false;
        logOrder("AgentStreamDone", { awaiting: awaitingPersistAiRef.current, source });
        // Rejoin replay of a finished stream: the persisted message is
        // already in the chat. Clear any tool-call/thinking buffers the
        // replay populated and bail — no refetch needed, no 6s backstop
        // needed, no live virtual.
        if (source === "replay") {
          setLiveStreamText(""); setLiveFinalizeArgs(null);
          setLiveThinkingList([]);
          setCurrentToolCalls([]);
          setActiveToolCalls(new Set());
          setAiProgressState("idle");
          setAiProgressStatus("");
          awaitingPersistAiRef.current = false;
          try { setAwaitingPersist(false); } catch {}
          setShowAIThinking(false);
          return;
        }
        try { awaitingPersistAiRef.current = true; setAwaitingPersist(true); } catch {}
        setActiveToolCalls(new Set());
        setAiProgressState("idle");
        setAiProgressStatus("");
        try { refetchSelectedChat && refetchSelectedChat(); } catch {}
        try { refetchChats && refetchChats(); } catch {}
        try { pollForRenamedTitle(chatId, titleAtSend); } catch {}
        // Backstop: if refetch never produces a persisted AI message
        // at the expected slot (e.g. order numbering shifted, or the
        // stale-state effect's match heuristic misses), force-clear
        // the streaming buffers after 6s so the bubble doesn't linger
        // alongside the persisted message. Without this, the live
        // virtual + persisted message both render — the exact
        // duplication the user reported. We gate on streamId so a
        // brand-new stream started in the gap isn't wiped.
        setTimeout(() => {
          if (currentStreamIdRef.current !== streamId) return;
          if (!awaitingPersistAiRef.current) return;
          setLiveStreamText(""); setLiveFinalizeArgs(null);
          setLiveThinkingList([]);
          setCurrentToolCalls([]);
          setActiveToolCalls(new Set());
          streamingMessageOrderRef.current = null;
          awaitingPersistAiRef.current = false;
          try { setAwaitingPersist(false); } catch {}
          setShowAIThinking(false);
          logOrder("AgentDoneBackstopCleared");
        }, 6000);
        // Defer dispose so the post-`done` `thread_renamed` event (server
        // generates the title AFTER persisting the assistant message)
        // can land. If `thread_renamed` arrives first, the handler above
        // disposes immediately; otherwise we clean up after 15s — but
        // only if the user hasn't started a new stream in the meantime
        // (compared via streamId rather than function identity).
        setTimeout(() => {
          if (currentStreamIdRef.current === streamId) {
            try { wsSubDisposeRef.current?.(); } catch {}
            wsSubDisposeRef.current = null;
            currentStreamIdRef.current = null;
          }
        }, 15000);
        logOrder("AgentDoneDeferredDispose");
      }
      if (ev.type === "error") {
        logOrder("AgentStreamError");
        setShowAIThinking(false);
        setLiveStreamText(""); setLiveFinalizeArgs(null);
        setLiveThinkingList([]);
        setCurrentToolCalls([]);
        setActiveToolCalls(new Set());
        setAiProgressState("idle");
        setAiProgressStatus("");
        try { wsSubDisposeRef.current?.(); } catch {}
        wsSubDisposeRef.current = null;
        currentStreamIdRef.current = null;
      }
    },
    onError: (err: unknown) => {
      // eslint-disable-next-line no-console
      console.error("[ChatStream] Subscription error (likely transient WS drop) — keeping UI state so reconnect can resume:", err);
      // Critical: DO NOT clear streaming UI state here. The previous
      // implementation nuked liveStreamText / currentStreamIdRef /
      // wsSubDisposeRef on every subscription error, which is exactly
      // what produced the "screen went blank after I clicked
      // mens_woven" bug: a transient WebSocket blip surfaced as a sink
      // error, the UI immediately wiped the streaming bubble, and even
      // though the backend kept producing events and graphql-ws
      // auto-resubscribed seconds later, the user just saw a blank
      // gap until done.
      //
      // The hook's reconnect handler now replays missed events via
      // agentChatStreamReplay (deduped by seq), so the right move on
      // subscription error is: log it, leave the streaming buffers
      // intact, leave currentStreamIdRef alone so the reconnect path
      // can rejoin, and trust the watchdog / done event to finalize.
      try {
        setAiProgressState("thinking");
        setAiProgressStatus("Reconnecting…");
      } catch {}
      try { refetchSelectedChat && refetchSelectedChat(); } catch {}
    },
    onComplete: () => {
      // eslint-disable-next-line no-console
      console.log("[ChatStream] Complete");
    },
  });

  // Auto-rejoin is declared further down in the file (after the
  // chat-change clearing effect) so its `streamingMessageOrderRef`
  // assignment isn't blown away by the clear-on-chat-change. React
  // runs effects in declaration order; the rejoin needs to win when
  // both fire in the same render cycle.

  // Watchdog: belt for the WS reconnect path. If isLoading is true
  // (we believe the agent is producing a response) but we haven't
  // received any event in WATCHDOG_TIMEOUT_MS, the socket has gone
  // silent without firing an error — happens with some proxies that
  // drop idle TCP sessions, or when the user's network briefly drops
  // and the OS quietly recycles the connection. Recovery: refetch the
  // chat to discover the current activeStreamId, then rejoin (replay
  // first → live subscribe). Dedupe by seq makes the replay safe even
  // if the WS was actually fine.
  useEffect(() => {
    if (!showAIThinking) return;
    if (!selectedChatId) return;
    const WATCHDOG_TIMEOUT_MS = 30_000;
    const CHECK_INTERVAL_MS = 5_000;
    // Seed so the watchdog has a baseline even if no event has landed
    // yet (e.g. the user just clicked Send and the first delta is
    // still in flight).
    if (!lastEventTsRef.current) lastEventTsRef.current = Date.now();
    const id = setInterval(() => {
      const sinceLast = Date.now() - lastEventTsRef.current;
      if (sinceLast < WATCHDOG_TIMEOUT_MS) return;
      const activeStreamId = (selectedChatData as { activeStreamId?: string | null } | undefined)?.activeStreamId;
      // eslint-disable-next-line no-console
      console.warn(`[Watchdog] No stream event in ${sinceLast}ms — refetching chat${activeStreamId ? ' + rejoining ' + activeStreamId : ''}`);
      try { refetchSelectedChat && refetchSelectedChat(); } catch {}
      if (activeStreamId) {
        const titleAtSend: string =
          (chatsData?.data as { _id: string; title?: string }[] | undefined)?.find?.((c) => c._id === selectedChatId)?.title ?? "";
        currentStreamIdRef.current = activeStreamId;
        // Tear down the (likely dead) sub so rejoin doesn't short-
        // circuit on isSubscribedTo. The replay+live re-attach is
        // what actually resumes the user's view.
        try { wsSubDisposeRef.current?.(); } catch {}
        wsSubDisposeRef.current = null;
        const dispose = stream.rejoin(
          activeStreamId,
          buildAgentStreamHandlers(selectedChatId, titleAtSend, activeStreamId)
        );
        wsSubDisposeRef.current = dispose;
      }
      // Reset the baseline so we don't re-trip the watchdog every
      // CHECK_INTERVAL_MS — give the recovery path a fresh window.
      lastEventTsRef.current = Date.now();
    }, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAIThinking, selectedChatId]);

  // Page-visibility recovery: when the tab regains focus (the user
  // came back from another window/tab) and we believe a stream is in
  // flight, refetch the chat so the auto-rejoin effect can pick up
  // the latest activeStreamId. Backgrounded tabs throttle setInterval
  // aggressively, so the watchdog above is unreliable in that state —
  // this is the catch-up handler for that case.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      if (!selectedChatId) return;
      try { refetchSelectedChat && refetchSelectedChat(); } catch {}
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [selectedChatId, refetchSelectedChat]);

  const [, { isLoading: createChatLoading }] = useCreateChatMutation();
  // Streaming client state
  const wsSubDisposeRef = useRef<null | (() => void)>(null);
  // Identifies the streamId we're currently subscribed to. Used by the
  // post-`done` 15s deferred dispose to avoid tearing down a brand-new
  // subscription if the user sent another message in the gap. Previously
  // this used a reference-equality check on the dispose function; with
  // the hook, dispose is a fresh function per subscribe call so we
  // compare streamIds instead.
  const currentStreamIdRef = useRef<string | null>(null);
  // Wall-clock timestamp of the most recent agent stream event we
  // processed. Drives the watchdog: if isLoading is true but we
  // haven't seen any event in WATCHDOG_TIMEOUT_MS, the watchdog
  // assumes the WebSocket connection silently died (some proxies drop
  // idle sockets without sending a close frame) and forces a refetch
  // + rejoin via the activeStreamId stamped on the chat doc.
  const lastEventTsRef = useRef<number>(0);
  // Tracks whether the most recent agent event was a thinking_delta. Lets
  // us coalesce a continuous run of thinking deltas into one row in
  // liveThinkingList (instead of one row per chunk), and reset that row
  // when the model transitions to text or tool calls.
  const isStreamingThoughtRef = useRef<boolean>(false);
  const awaitingPersistAiRef = useRef<boolean>(false);
  // Track which messageOrder we're currently streaming for
  const streamingMessageOrderRef = useRef<number | null>(null);
  // Track the messageOrder of a previous stream that we're awaiting persistence for
  // (used when clicking UI options while a stream is active - we preserve the first response)
  const awaitingPersistMessageOrderRef = useRef<number | null>(null);
  // Track the streaming content from a previous stream that we're preserving
  // (used when clicking UI options while streaming - we keep showing the first response)
  const preservedStreamingContentRef = useRef<{
    text: string;
    thinkingList?: string[];
    toolCalls: { name: string; args: any }[];
  } | null>(null);
  // Track the highest messageOrder we've seen from persisted messages (to detect stale streaming state)
  const highestPersistedMessageOrderRef = useRef<number | null>(null);
  const deletedSelectionHandledRef = useRef<boolean>(false);

  // Sync the access token into window for non-hook access (legacy
  // callers that read `window.__TD_ACCESS_TOKEN__` for graphql-request
  // headers). Kept here so the hook below can also pick up the latest
  // token via the same window fallback if redux hasn't hydrated yet.
  useEffect(() => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const store = require("@/store");
      const st = store?.default?.getState?.();
      const reduxToken = st?.auth?.accessToken;
      if (reduxToken) (window as any).__TD_ACCESS_TOKEN__ = reduxToken;
    } catch {}
  }, [accessToken]);

  // WebSocket lifecycle, auto-reconnect, replay-then-subscribe, and
  // active-subscription tracking all live in this hook. The chat
  // advisor never touches the underlying graphql-ws client directly any
  // more — it just calls stream.subscribe / stream.rejoin.
  const stream = useAgentChatStream({ accessToken });

  const chatSessions: UIChatSession[] = chatsData?.data?.map(convertApiChatToUI) || [];
  // Ensure sidebar reflects a just-created chat immediately even before the list refetch returns,
  // and keep items sorted by most-recent activity (timestamp desc) to match Figma
  const sidebarSessions: UIChatSession[] = useMemo(() => {
    const list = [...chatSessions];
    if (selectedChatId && !list.some((c) => c.id === selectedChatId)) {
      list.unshift({
        id: selectedChatId,
        title: "New Chat",
        timestamp: new Date().toISOString(),
        lastMessage: "",
        messages: [],
      });
    }
    list.sort((a, b) => {
      const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return bTime - aTime;
    });
    return list;
  }, [chatSessions, selectedChatId]);
  // Group chats into Today / This Week / 7 days or older buckets for sidebar
  const groupedSidebarSessions = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const oneDayMs = 24 * 60 * 60 * 1000;

    const groups: { label: string; items: UIChatSession[] }[] = [
      { label: "Today", items: [] },
      { label: "This Week", items: [] },
      { label: "7 days or older", items: [] },
    ];

    sidebarSessions.forEach((chat) => {
      if (!chat.timestamp) {
        groups[2].items.push(chat);
        return;
      }
      const ts = new Date(chat.timestamp);
      const startOfChatDay = new Date(ts.getFullYear(), ts.getMonth(), ts.getDate()).getTime();
      const diffDays = Math.floor((startOfToday - startOfChatDay) / oneDayMs);

      if (diffDays <= 0) {
        groups[0].items.push(chat);
      } else if (diffDays < 7) {
        groups[1].items.push(chat);
      } else {
        groups[2].items.push(chat);
      }
    });

    return groups.filter((g) => g.items.length > 0);
  }, [sidebarSessions]);
  // Memoize selectedChatFromAPI to prevent unnecessary re-renders
  // This was causing infinite re-render loops because convertApiChatToUI creates new objects
  const selectedChatFromAPI = useMemo(() => {
    if (!selectedChatId) return undefined;
    if (selectedChatData) return convertApiChatToUI(selectedChatData);
    return chatSessions.find(chat => chat.id === selectedChatId);
  }, [selectedChatId, selectedChatData, chatSessions]);
  // If we have a selectedChatId but the API hasn't returned it yet, synthesize a stub so the UI switches immediately
  const selectedChatFallback: UIChatSession | undefined = (!selectedChatFromAPI && selectedChatId)
    ? { id: selectedChatId, title: "New Chat", timestamp: new Date().toISOString(), lastMessage: "", messages: [] }
    : undefined;
  
  // When messages are loaded from API, check if streaming state is stale (message already persisted)
  // This runs after selectedChatFromAPI is defined, so we can check for stale streaming state
  useEffect(() => {
    const apiMsgs = selectedChatFromAPI?.messages || [];
    if (apiMsgs.length === 0) return;
    
    // Find the highest messageOrder from persisted messages
    let highestOrder = highestPersistedMessageOrderRef.current;
    for (const msg of apiMsgs) {
      const msgOrder = (msg as any).messageOrder;
      if (msgOrder != null && (highestOrder === null || msgOrder > highestOrder)) {
        highestOrder = msgOrder;
      }
    }
    
    // Update the highest order we've seen
    if (highestOrder !== null) {
      highestPersistedMessageOrderRef.current = highestOrder;
    }
    
    // If we're streaming for a messageOrder that's already persisted, clear streaming state
    if (streamingMessageOrderRef.current !== null && highestOrder !== null) {
      const streamingOrder = streamingMessageOrderRef.current;

      // Check if we have a persisted message for this streaming order
      const persistedForStreamingOrder = apiMsgs.find(
        (msg: any) => (msg?.source === 'AI' || msg?.sender === 'AI') &&
        msg?.messageOrder === streamingOrder
      );

      // Also check if the streaming order is less than or equal to highest persisted order
      // (meaning it's definitely already persisted)
      const isStaleStreaming = streamingOrder <= highestOrder;

      // Backstop for off-by-one frontend order guesses. The expected
      // AI messageOrder is computed at send-time as `lastOrder + 2`,
      // but the backend can persist at a different slot if numbering
      // shifted (other writers, retries, etc). Without this fallback,
      // the live streaming virtual + persisted AI message both
      // render — the exact duplication shown in the bug report. We
      // only apply it once `done` has fired (awaitingPersistAi is
      // true) AND an AI message has actually landed at or near the
      // expected slot, so we never collapse the bubble while the
      // stream is mid-flight.
      const doneAndAiPersistedNearStreamingSlot =
        awaitingPersistAiRef.current &&
        apiMsgs.some(
          (msg: any) =>
            (msg?.source === 'AI' || msg?.sender === 'AI') &&
            (msg?.messageOrder ?? -1) >= streamingOrder - 1
        );

      if (persistedForStreamingOrder || isStaleStreaming || doneAndAiPersistedNearStreamingSlot) {
        if (DEBUG_STREAMING) console.log('[StreamingState] Clearing stale streaming state - message already persisted', {
          streamingOrder,
          highestPersistedOrder: highestOrder,
          persistedMessageFound: !!persistedForStreamingOrder,
          isStaleStreaming,
        });
        
        // Kill the gating flags so the live virtual stops being added and
        // the standalone streaming bubble hides, but keep liveStreamText /
        // liveThinking* / currentToolCalls populated until the next stream
        // starts (sendStreamingMessage clears them). Holding the snapshot
        // keeps a renderable fallback if Apollo's refetch is delayed and
        // avoids racing the persisted node's mount.
        setShowAIThinking(false);
        awaitingPersistAiRef.current = false;
        setAwaitingPersist(false);
        streamingMessageOrderRef.current = null;
        
        // Only clear optimistic messages that match the specific streaming order we're clearing
        // Don't clear all optimistic messages without messageOrder - they might be legitimate
        if (streamingOrder != null) {
          setOptimisticMessages((prev) => 
            prev.filter((msg) => 
              !(msg.sender === MessageSender.AI && msg.messageOrder === streamingOrder)
            )
          );
        }
        
        // Clear any retry intervals
        if ((window as any).__chatStreamRetryInterval) {
          clearInterval((window as any).__chatStreamRetryInterval);
          (window as any).__chatStreamRetryInterval = null;
        }
      }
    }
  }, [selectedChatFromAPI?.messages]);
  
  // Hoisted helper to strip Generative UI JSON from message content
  function stripUiSpecFromContent(content: string): string {
    if (!content) return '';
    let cleaned = content;
    
    // First remove fenced UI blocks ```ui ... ``` (handles various whitespace/newline patterns)
    // Match ```ui followed by optional whitespace, then content, then closing ```
    cleaned = cleaned.replace(/```\s*ui\s*[\s\S]*?```/gi, "").trim();
    
    // Then remove any standalone JSON UI specs that might have been left behind
    if (/"type"\s*:\s*"choice"/.test(cleaned) && /"options"\s*:\s*\[/.test(cleaned)) {
      cleaned = cleaned.replace(/\{[\s\S]*?"type"\s*:\s*"choice"[\s\S]*?"options"\s*:\s*\[[\s\S]*?\][\s\S]*?\}/g, "").trim();
    }
    
    // Clean up any leftover empty lines or extra whitespace
    cleaned = cleaned.replace(/\n\s*\n\s*\n/g, '\n\n').trim();
    
    return cleaned;
  }

  const mergedMessages = useMemo(() => {
    const apiMsgs = selectedChatFromAPI?.messages || [];
    
    // Filter optimistic messages: exclude those that match persisted messages by messageOrder or content
    const filteredOptimistic = optimisticMessages.filter((om) => {
      // Check if there's a persisted message with the same messageOrder
      if (om.messageOrder != null) {
        const hasPersistedWithSameOrder = apiMsgs.some((am) => 
          am.messageOrder === om.messageOrder && am.sender === om.sender
        );
        if (hasPersistedWithSameOrder) {
          return false; // Skip optimistic if persisted exists with same messageOrder
        }
      }
      
      // For optimistic messages without messageOrder, check if they match any persisted message by content
      // Only filter them out if they're actually duplicates, not just because persisted messages exist
      if (om.messageOrder == null && om.sender === MessageSender.AI) {
        const normalize = (s: string) => stripUiSpecFromContent(s || '').trim().replace(/\s+/g, ' ');
        const optContent = normalize(om.content || '');
        
        // Check if this optimistic message matches any persisted AI message by content
        const matchesPersisted = apiMsgs.some((am) => {
          if (am.sender !== MessageSender.AI) return false;
          const apiContent = normalize(am.content || '');
          // Match if content is very similar (allowing for small differences)
          return apiContent.length > 0 && 
                 (apiContent === optContent || 
                  Math.abs(apiContent.length - optContent.length) < 100);
        });
        
        if (matchesPersisted) {
          // This optimistic message matches a persisted one, filter it out
          return false;
        }
      }
      
      // Fallback: check by exact content matching
      return !apiMsgs.some((am) => {
        if (am.sender !== om.sender) return false;
        const apiContent = am.sender === MessageSender.AI
          ? stripUiSpecFromContent(am.content || '').trim()
          : (am.content || '').trim();
        const optContent = (om.content || '').trim();
        return apiContent === optContent;
      });
    });
    
    // Merge: API messages first (they're persisted and have correct data)
    const merged = [...apiMsgs, ...filteredOptimistic];
    
    // If we have preserved streaming content, add it as a virtual message for correct sorting
    // This ensures the preserved first response appears in the correct position relative to optimistic messages
    // If we have preserved streaming content from a previous stream, add it as a virtual message
    if (awaitingPersistMessageOrderRef.current !== null && preservedStreamingContentRef.current) {
      const preservedOrder = awaitingPersistMessageOrderRef.current;
      const hasPersistedWithThisOrder = merged.some(
        (m) => m.messageOrder === preservedOrder && m.sender === MessageSender.AI
      );
      const hasVirtualPreserved = merged.some((m: any) => (m as any)._isPreservedStreaming && m.messageOrder === preservedOrder);
      if (!hasPersistedWithThisOrder && !hasVirtualPreserved) {
        logOrder('AddPreservedVirtual', { order: preservedOrder });
        merged.push({
          id: `preserved-streaming-${preservedOrder}`,
          content: preservedStreamingContentRef.current.text || '',
          sender: MessageSender.AI,
          timestamp: new Date().toISOString(),
          messageOrder: preservedOrder,
          llmMetadata: {
            thinking: preservedStreamingContentRef.current.thinkingList
              ? [...preservedStreamingContentRef.current.thinkingList]
              : [],
            toolCalls: preservedStreamingContentRef.current.toolCalls.map(tc => ({
              name: tc.name,
              args: tc.args,
            })),
          },
          _isPreservedStreaming: true,
        } as UIChatMessage & { _isPreservedStreaming?: boolean });
      }
    }
    
    // If we have an active current stream (not preserved), add it as a virtual message too
    // so it sorts in the right place immediately (below the corresponding user message).
    //
    // Fallback for cold-rejoin races: when the user navigates back to a chat
    // mid-stream, the clearing effect nulls streamingMessageOrderRef before
    // the auto-rejoin effect can refill it (or before Apollo loads the chat
    // data needed to compute the right order). Without a fallback, the live
    // virtual never mounts during that window, and the bottom-streaming-
    // bubble + unified-isLoading fallbacks both render — producing the
    // duplicate "Reasoning..." bubbles. So if there's live content but no
    // anchor order, infer it as one past the last persisted message.
    const hasActiveStreamingContent = !!(liveStreamText || (liveThinkingList.length > 0) || currentToolCalls.length > 0);
    let activeStreamOrder = streamingMessageOrderRef.current;
    if (activeStreamOrder == null && hasActiveStreamingContent && apiMsgs.length > 0) {
      // Cold-rejoin race only — gate on "stream still in flight" (USER is the
      // last persisted message). Without this gate, a click-out/click-back AFTER
      // the AI message persists computes maxApiOrder+1 (one past the persisted
      // AI), and the live virtual stamps a duplicate bubble right below it.
      const lastApiMsg = apiMsgs[apiMsgs.length - 1];
      const streamStillInFlight =
        lastApiMsg?.sender === MessageSender.USER || awaitingPersistAiRef.current;
      if (streamStillInFlight) {
        const maxApiOrder = Math.max(...apiMsgs.map((m) => m.messageOrder ?? -1));
        activeStreamOrder = maxApiOrder + 1;
      }
    }
    if (activeStreamOrder != null && hasActiveStreamingContent) {
      const hasPersistedForActiveOrder = merged.some((m) =>
        m.messageOrder === activeStreamOrder && m.sender === MessageSender.AI
      );
      const hasVirtualAlready = merged.some((m: any) =>
        (m as any)._isLiveStreaming === true && m.messageOrder === activeStreamOrder
      );
      // Content-based escape hatch: when the order math is off (the post-
      // mutation re-computation racing the RTK refetch can make
      // streamingMessageOrderRef.current point at a slot the AI message
      // never lands in), `hasPersistedForActiveOrder` stays false, and
      // the virtual gets stamped right next to the real persisted message.
      // If ANY persisted AI message already carries the same finalize
      // envelope, the stream is done and the virtual is redundant —
      // mirror of the AIProgressIndicator dedup that fixed the thinking
      // box duplicating.
      const liveMd = liveFinalizeArgs?.analysis_markdown || liveStreamText || '';
      const hasPersistedMatchingContent = liveMd.length > 0 && apiMsgs.some((m: any) => {
        if (m?.sender !== MessageSender.AI && m?.source !== 'AI') return false;
        const persistedMd = m?.finalizeArgs?.analysis_markdown ?? m?.content ?? '';
        return persistedMd === liveMd;
      });
      if (!hasPersistedForActiveOrder && !hasVirtualAlready && !hasPersistedMatchingContent) {
        logOrder('AddLiveVirtual', { order: activeStreamOrder });
        merged.push({
          id: `live-streaming-${activeStreamOrder}`,
          content: liveStreamText || '',
          sender: MessageSender.AI,
          timestamp: new Date().toISOString(),
          messageOrder: activeStreamOrder,
          llmMetadata: {
            thinking: [...liveThinkingList],
            toolCalls: currentToolCalls.map(tc => ({
              name: tc.name,
              args: tc.args,
            })),
          },
          _isLiveStreaming: true,
        } as UIChatMessage & { _isLiveStreaming?: boolean });
      }
    }
    
    // Sort by messageOrder if available (from API), otherwise by timestamp
    merged.sort((a, b) => {
      const aOrder = a.messageOrder;
      const bOrder = b.messageOrder;
      // If both have messageOrder, use it for sorting
      if (aOrder != null && bOrder != null) {
        return aOrder - bOrder;
      }
      // If only one has messageOrder, prioritize it (API messages come before optimistic)
      if (aOrder != null && bOrder == null) return -1;
      if (bOrder != null && aOrder == null) return 1;
      // Fallback to timestamp comparison for optimistic messages without messageOrder
      const aTime = new Date(a.timestamp || 0).getTime();
      const bTime = new Date(b.timestamp || 0).getTime();
      return aTime - bTime;
    });
    logOrder('MergedSorted', merged.map((m: any) => ({
      id: m.id,
      sender: m.sender,
      order: m.messageOrder,
      live: (m as any)._isLiveStreaming ? true : undefined,
      preserved: (m as any)._isPreservedStreaming ? true : undefined,
    })));
    
    // Remove the duplicate sort that was added - we already sorted above
    // The messageOrder-based sort should handle everything correctly
    
    // Deduplicate messages: prefer persisted messages (from API) over optimistic ones
    const normalize = (s: string) => stripUiSpecFromContent(s || '').trim().replace(/\s+/g, ' ');
    const deduped: UIChatMessage[] = [];
    const seenMessageOrders = new Set<string>(); // Track seen messageOrder+sender combinations
    
    for (const m of merged) {
      // Primary deduplication: Use messageOrder if available (more reliable than content)
      if (m.messageOrder != null && m.sender) {
        const orderKey = `${m.sender}-${m.messageOrder}`;
        if (seenMessageOrders.has(orderKey)) {
          // Skip duplicate by messageOrder - the first one (from API) is already in deduped
          // This ensures persisted messages replace optimistic ones
          logOrder('DedupSkipByOrder', { id: (m as any).id, sender: m.sender, order: m.messageOrder });
          continue;
        }
        seenMessageOrders.add(orderKey);
      }
      
      // Secondary deduplication: Content-based matching ONLY for messages without messageOrder
      // Avoid removing persisted or virtual (ordered) messages by content similarity
      const prev = deduped[deduped.length - 1];
      const prevHasOrder = prev?.messageOrder != null;
      const currHasOrder = m.messageOrder != null;
      if (!prevHasOrder && !currHasOrder && prev && prev.sender === m.sender && normalize(prev.content) === normalize(m.content || '')) {
        continue;
      }
      deduped.push(m);
    }
    return deduped;
  }, [selectedChatFromAPI?.messages, optimisticMessages, liveStreamText, liveThinkingList, currentToolCalls.length]);

  const selectedChat = (selectedChatFromAPI || selectedChatFallback)
    ? {
        ...(selectedChatFromAPI || selectedChatFallback!),
        messages: mergedMessages,
      }
    : undefined;

  const isLoading = showAIThinking;
  // Collapse main sidebar when entering ChatAdvisor
  useEffect(() => {
    try {
      window.dispatchEvent(new CustomEvent('td:setSidebarExtended', { detail: false }));
    } catch {}
  }, []);

  useEffect(() => {
    if (showChatInterface && inputRef.current && !isLoading) {
      // Don't steal focus if user is selecting text
      const selection = window.getSelection();
      const hasActiveSelection = selection && selection.toString().length > 0;
      if (!hasActiveSelection) {
        inputRef.current.focus();
      }
    }
  }, [showChatInterface, selectedChatId, isLoading]);

  useEffect(() => {
    setOptimisticMessages([]);
    setLastMessageCount(0);
    setLiveStreamText(""); setLiveFinalizeArgs(null);
    setLiveThinkingList([]);
    setCurrentToolCalls([]);
    setActiveToolCalls(new Set());
    setStreamingCode(null);
    setDutyCalculatorStates({});
    // If the chat is deselected, ensure any active subscription is closed
    if (!selectedChatId) {
      if (wsSubDisposeRef.current) {
        try { wsSubDisposeRef.current(); } catch {}
        wsSubDisposeRef.current = null;
      }
      try { setShowAIThinking(false); } catch {}
      try { awaitingPersistAiRef.current = false; setAwaitingPersist(false); } catch {}
      try { awaitingPersistMessageOrderRef.current = null; } catch {}
      try { preservedStreamingContentRef.current = null; } catch {}
    }
    streamingMessageOrderRef.current = null;
    awaitingPersistMessageOrderRef.current = null;
    highestPersistedMessageOrderRef.current = null;
  }, [selectedChatId]);

  // Auto-rejoin an in-flight stream when the user (re-)enters a chat
  // that has chat.activeStreamId set on the server. This is what fixes
  // "I refreshed mid-response and now the chat is stuck waiting" — the
  // hook fetches buffered events via agentChatStreamReplay, replays
  // them through the handlers above, then opens the live subscription
  // for any tail events. Idempotent: if we're already subscribed to
  // this streamId (e.g. just-sent message), the hook returns the
  // existing dispose and this is a no-op.
  //
  // Declared AFTER the chat-change clearing effect above so that the
  // assignment to `streamingMessageOrderRef.current` survives — React
  // runs effects in declaration order, and the clearing effect resets
  // that ref to null. Without this ordering, the live virtual never
  // mounts on rejoin and the bottom-streaming-bubble + unified-
  // isLoading fallbacks render at the same time, producing two
  // identical "Reasoning..." bubbles.
  useEffect(() => {
    const activeStreamId = (selectedChatData as { activeStreamId?: string | null } | undefined)?.activeStreamId;
    if (!activeStreamId || !selectedChatId) return;
    if (stream.isSubscribedTo(activeStreamId)) return;
    // Stale activeStreamId guard: the backend clears chat.activeStreamId
    // when a stream ends, but the frontend's cached `selectedChatData`
    // may still carry the old value after the user navigates away and
    // back. If the latest persisted message is already an AI reply, the
    // response is on disk — replaying the buffer would just populate
    // live state (thinking + tools) next to the persisted message,
    // producing a duplicate render.
    const apiMsgs = (selectedChatData as { messages?: { messageOrder: number; sender?: string; source?: string }[] } | undefined)?.messages ?? [];
    const lastMsg = apiMsgs[apiMsgs.length - 1];
    if (lastMsg && (lastMsg.sender === 'AI' || lastMsg.source === 'AI')) {
      return;
    }
    const titleAtSend: string =
      (chatsData?.data as { _id: string; title?: string }[] | undefined)?.find?.((c) => c._id === selectedChatId)?.title ?? "";
    setShowAIThinking(true);
    currentStreamIdRef.current = activeStreamId;
    // Compute the messageOrder the in-flight AI message will land at so
    // the inline live virtual takes over from the fallbacks.
    const maxOrder = apiMsgs.length > 0
      ? Math.max(...apiMsgs.map((m) => m.messageOrder ?? -1))
      : -1;
    streamingMessageOrderRef.current = maxOrder + 1;
    const dispose = stream.rejoin(
      activeStreamId,
      buildAgentStreamHandlers(selectedChatId, titleAtSend, activeStreamId)
    );
    wsSubDisposeRef.current = dispose;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChatId, (selectedChatData as { activeStreamId?: string | null } | undefined)?.activeStreamId]);

  // On unmount, make sure any active subscription is torn down to avoid UI carryover
  useEffect(() => {
    return () => {
      if (wsSubDisposeRef.current) {
        try { wsSubDisposeRef.current(); } catch {}
        wsSubDisposeRef.current = null;
      }
      // Do not call setState here to avoid updates on unmounted component
    };
  }, []);

  // Universal "a new AI message landed → drop the live stream snapshot."
  //
  // Tracks the count of persisted AI messages; whenever it increments,
  // the live state for the just-resolved stream is wiped. This catches
  // every path that leaves a duplicate (rejoin replay, cold-rejoin
  // races, off-by-one streamingMessageOrderRef, watchdog re-attaches,
  // etc.) without depending on content matching, order math, or the
  // `awaitingPersistAiRef` flag — that flag gets cleared by the replay-
  // source `done` handler before the persisted message arrives, which
  // is exactly the rejoin scenario this guard exists to cover.
  const persistedAiCountRef = useRef<number>(0);
  useEffect(() => {
    const apiMsgs = (selectedChatFromAPI as { messages?: { sender?: string; source?: string; messageOrder?: number }[] } | undefined)?.messages ?? [];
    let aiCount = 0;
    for (const m of apiMsgs) {
      if (m.sender === 'AI' || m.source === 'AI') aiCount++;
    }
    if (aiCount <= persistedAiCountRef.current) {
      persistedAiCountRef.current = aiCount; // handles reset on chat switch
      return;
    }
    persistedAiCountRef.current = aiCount;
    // A new AI message just persisted; drop any lingering live snapshot.
    setShowAIThinking(false);
    setLiveStreamText("");
    setLiveFinalizeArgs(null);
    setLiveThinkingList([]);
    setCurrentToolCalls([]);
    setActiveToolCalls(new Set());
    setStreamingCode(null);
    awaitingPersistAiRef.current = false;
    try { setAwaitingPersist(false); } catch {}
    streamingMessageOrderRef.current = null;
  }, [selectedChatFromAPI?.messages, selectedChatId]);

  // Measure overlay heights so chat content can wrap around them while they float
  useLayoutEffect(() => {
    const measure = () => {
      try { setOverlayTopPx(Math.round(topOverlayRef.current?.getBoundingClientRect()?.height || 0)); } catch {}
      try { setOverlayBottomPx(Math.round(bottomOverlayRef.current?.getBoundingClientRect()?.height || 0)); } catch {}
    };
    measure();
    const ro = (window as any).ResizeObserver ? new (window as any).ResizeObserver(measure) : null;
    try { ro?.observe?.(topOverlayRef.current); } catch {}
    try { ro?.observe?.(bottomOverlayRef.current); } catch {}
    window.addEventListener('resize', measure);
    return () => { try { ro?.disconnect?.(); } catch {} window.removeEventListener('resize', measure); };
  }, []);

  // Clear selected chat when client changes
  useEffect(() => {
    // Pending attachments belong to the previous client/chat context.
    clearAttachments();
    setSelectedChatId(null);
    setShowChatInterface(false);
    // Reset auto-create guards on client change to allow a fresh auto-creation
    autoCreatedRef.current = false;
    createInFlightRef.current = null;
  }, [selectedClient]);

  useEffect(() => {
    const currentMessageCount = selectedChatFromAPI?.messages.length || 0;

    // Clear optimistic user echo only after an AI message is persisted
    const lastApiMsg = (selectedChatFromAPI as any)?.messages?.[currentMessageCount - 1];
    const lastSource = lastApiMsg?.source;
    // If the persisted USER message has arrived, drop the optimistic USER echo immediately to avoid double display
    if (optimisticMessages.length > 0 && lastSource === 'USER') {
      const lastOptimistic = optimisticMessages[optimisticMessages.length - 1];
      if (lastOptimistic?.sender === MessageSender.USER && lastOptimistic?.content === lastApiMsg?.content) {
        setOptimisticMessages([]);
      }
    }
    if (optimisticMessages.length > 0 && lastSource === 'AI') {
      setTimeout(() => {
        setOptimisticMessages([]);
      }, 100);
    }

    setLastMessageCount(currentMessageCount);
  }, [selectedChatFromAPI?.messages.length, optimisticMessages.length, lastMessageCount]);

  // When streaming completes, wait to clear the live bubble until the persisted AI message is visible
  useEffect(() => {
    if (!awaitingPersistAiRef.current || (streamingMessageOrderRef.current === null && awaitingPersistMessageOrderRef.current === null)) {
      if (DEBUG_STREAMING) console.log('[StreamingState] Not awaiting persistence or no streaming messageOrder, skipping check');
      return;
    }
    
    // Check for the messageOrder we're awaiting persistence for
    // Prioritize awaitingPersistMessageOrderRef (previous stream) over streamingMessageOrderRef (current stream)
    const expectedMessageOrder = awaitingPersistMessageOrderRef.current ?? streamingMessageOrderRef.current;
    if (expectedMessageOrder === null) {
      if (DEBUG_STREAMING) console.log('[StreamingState] No messageOrder to check, skipping');
      return;
    }
    const currentMessageCount = selectedChatFromAPI?.messages?.length || 0;
    if (DEBUG_STREAMING) console.log('[StreamingState] Checking for persisted message:', {
      expectedMessageOrder,
      messageCount: currentMessageCount,
      liveStreamTextLength: liveStreamText?.length || 0,
      awaitingPersistAiRef: awaitingPersistAiRef.current,
    });
    
    if (currentMessageCount === 0) {
      if (DEBUG_STREAMING) console.log('[StreamingState] No messages yet, waiting...');
      return;
    }
    
    // Find the AI message with the expected messageOrder (not just the last one).
    // Backup match: when the order guess is off (handleExampleMessageClick +
    // sendStreamingMessage compute it from stale snapshots, so for a brand-new
    // chat the ref can be off-by-one), fall back to the most recent AI message
    // whose finalizeArgs.analysis_markdown matches the live envelope. That's a
    // strong "stream is done and persisted" signal regardless of order math.
    const msgsForLookup = ((selectedChatFromAPI as any)?.messages ?? []) as any[];
    let targetMessage = msgsForLookup.find(
      (msg: any) => (msg?.source === 'AI' || msg?.sender === 'AI') && msg?.messageOrder === expectedMessageOrder
    );
    if (!targetMessage && liveFinalizeArgs?.analysis_markdown) {
      const liveMd = liveFinalizeArgs.analysis_markdown;
      for (let i = msgsForLookup.length - 1; i >= 0; i--) {
        const m = msgsForLookup[i];
        if (m?.source !== 'AI' && m?.sender !== 'AI') continue;
        const persistedMd = m?.finalizeArgs?.analysis_markdown ?? m?.content ?? '';
        if (persistedMd && persistedMd === liveMd) {
          targetMessage = m;
          break;
        }
      }
    }
    
    if (!targetMessage) {
      if (DEBUG_STREAMING) console.log('[StreamingState] Message with order', expectedMessageOrder, 'not found yet, waiting...');
      return;
    }
    
    // Check if this message was created very recently (within last 15 seconds)
    const msgTime = new Date(targetMessage?.createdAt || targetMessage?.timestamp || 0).getTime();
    const timeSinceCreated = Date.now() - msgTime;
    const isRecentMessage = timeSinceCreated < 15000; // 15 seconds
    
    if (DEBUG_STREAMING) console.log('[StreamingState] Found target AI message:', {
      messageId: targetMessage?.id,
      messageOrder: targetMessage?.messageOrder,
      timeSinceCreated,
      isRecentMessage,
      awaitingPersistence: awaitingPersistAiRef.current,
    });
    
    // Compare normalized content to ensure we're clearing the right streaming message
    // Use preserved content if available (from previous stream), otherwise use current streaming content
    const streamingTextToCompare = preservedStreamingContentRef.current?.text || liveStreamText;
    const persistedContent = stripUiSpecFromContent(targetMessage?.content || '');
    const streamingContent = stripUiSpecFromContent(streamingTextToCompare || '');
    const norm = (s: string) => s.trim().replace(/\s+/g, ' ');
    
    // Also check raw content (before stripping) to handle UI-only messages
    const rawPersistedContent = (targetMessage?.content || '').trim();
    const rawStreamingContent = (streamingTextToCompare || '').trim();
    
    const normalizedPersisted = norm(persistedContent);
    const normalizedStreaming = norm(streamingContent);
    
    // Check if raw content matches (handles UI-only messages where stripping removes everything)
    // Also handle case where persisted has MORE content (full response) than streaming (just UI block)
    const rawLengthDiff = Math.abs(rawPersistedContent.length - rawStreamingContent.length);
    const rawContentSimilar = rawPersistedContent.length > 0 && rawStreamingContent.length > 0 && 
        (rawLengthDiff < 200 || 
         // If persisted is longer, check if streaming content is contained in persisted
         (rawPersistedContent.length > rawStreamingContent.length && 
          rawPersistedContent.includes(rawStreamingContent)) ||
         // If streaming is longer (shouldn't happen but handle it), check if persisted is contained
         (rawStreamingContent.length > rawPersistedContent.length && 
          rawStreamingContent.includes(rawPersistedContent)));
    
    // Check if normalized content matches (standard case)
    // Also handle case where persisted has MORE content than streaming
    const normalizedLengthDiff = Math.abs(normalizedPersisted.length - normalizedStreaming.length);
    const normalizedMatches = normalizedPersisted.length > 0 && 
        (normalizedPersisted === normalizedStreaming || 
         normalizedLengthDiff < 100 ||
         // If persisted is longer, check if streaming content is contained in persisted
         (normalizedPersisted.length > normalizedStreaming.length && 
          normalizedPersisted.includes(normalizedStreaming) &&
          normalizedStreaming.length > 0) ||
         // If streaming is longer, check if persisted is contained
         (normalizedStreaming.length > normalizedPersisted.length && 
          normalizedStreaming.includes(normalizedPersisted) &&
          normalizedPersisted.length > 0));
    
    // Special case: If we have raw streaming content but normalized is empty (UI-only message),
    // and persisted has content, check if the raw streaming content is in persisted
    const uiOnlyMatch = normalizedStreaming.length === 0 && 
                        rawStreamingContent.length > 0 && 
                        rawPersistedContent.length > 0 &&
                        rawPersistedContent.includes(rawStreamingContent);
    
    // Fallback: If we have a recent AI message (created within last 15 seconds) and we're awaiting persistence,
    // accept it even if content doesn't match exactly (persisted might have more formatting)
    // This prevents the 3-second blank screen when content doesn't match exactly
    const isRecentMatch = isRecentMessage && 
                          awaitingPersistAiRef.current && 
                          rawPersistedContent.length > 0 &&
                          // Ensure persisted has at least some content (not empty)
                          (normalizedPersisted.length > 0 || rawPersistedContent.length > 50);
    
    // Detect formatted response: persisted message is significantly longer and contains the streamed content
    // This handles cases where backend formats the response with markdown/HTS codes after streaming
    const isFormattedResponse = isRecentMessage &&
                                awaitingPersistAiRef.current &&
                                rawPersistedContent.length > rawStreamingContent.length * 1.5 && // Persisted is at least 50% longer
                                rawStreamingContent.length > 0 &&
                                rawPersistedContent.includes(rawStreamingContent); // Persisted contains streamed content
    
    if (DEBUG_STREAMING) console.log('[StreamingState] Comparing persisted vs streaming:', {
      rawPersistedLength: rawPersistedContent.length,
      rawStreamingLength: rawStreamingContent.length,
      rawLengthDiff,
      rawContentSimilar,
      persistedLength: normalizedPersisted.length,
      streamingLength: normalizedStreaming.length,
      normalizedLengthDiff,
      normalizedMatches,
      uiOnlyMatch,
      isRecentMatch,
      isFormattedResponse,
      isRecentMessage,
      timeSinceCreated,
      streamingContainedInPersisted: normalizedPersisted.includes(normalizedStreaming),
      willClear: normalizedMatches || rawContentSimilar || uiOnlyMatch || isRecentMatch || isFormattedResponse,
    });
    
    // Clear if either normalized content matches OR raw content is very similar OR UI-only match OR recent message match OR formatted response
    // This handles both standard messages and UI-only messages, plus timing edge cases and formatted responses
    if (normalizedMatches || rawContentSimilar || uiOnlyMatch || isRecentMatch || isFormattedResponse) {
      // Clear all streaming states now that persisted message is visible
      if (DEBUG_STREAMING) console.log('[StreamingState] Clearing streaming state - persisted message matches', {
        reason: normalizedMatches ? 'normalized match' : 
                rawContentSimilar ? 'raw content similar' : 
                uiOnlyMatch ? 'UI-only match' : 
                isFormattedResponse ? 'formatted response (longer)' :
                'recent message match',
        messageOrder: expectedMessageOrder,
      });
      
      // Clear any retry intervals
      if ((window as any).__chatStreamRetryInterval) {
        clearInterval((window as any).__chatStreamRetryInterval);
        (window as any).__chatStreamRetryInterval = null;
      }
      
      // Persisted message has landed and matches; clear the live state
      // so the bottom streaming bubble + live virtual fall off in the
      // next paint. Historically these were held as a fallback for a
      // delayed refetch, but persist-arrival only fires when the
      // refetch landed — the persisted message is in `messages` right
      // now. The stable `ai-${messageOrder}` key prevents the blank
      // frame the old comment was worried about.
      try {
        setShowAIThinking(false);
        setLiveStreamText("");
        setLiveFinalizeArgs(null);
        setLiveThinkingList([]);
        setCurrentToolCalls([]);
        setActiveToolCalls(new Set());
        setStreamingCode(null);
      } catch {}
      awaitingPersistAiRef.current = false;
      try { setAwaitingPersist(false); } catch {}

      // Clear the preserved streaming content if this was the message we were preserving
      if (awaitingPersistMessageOrderRef.current === expectedMessageOrder) {
        awaitingPersistMessageOrderRef.current = null;
        preservedStreamingContentRef.current = null;
      }

      // Only clear streamingMessageOrderRef if it matches what we just persisted.
      // This is what flips `hasActiveStreamingContent`-gated branches off so
      // the live virtual stops being added even though its state lingers.
      if (streamingMessageOrderRef.current === expectedMessageOrder) {
        streamingMessageOrderRef.current = null;
      }
      
      // Only clear optimistic messages that match the specific messageOrder we just persisted
      // Don't clear all optimistic messages without messageOrder - they might be legitimate
      if (expectedMessageOrder != null) {
        setOptimisticMessages((prev) => 
          prev.filter((msg) => 
            !(msg.sender === MessageSender.AI && msg.messageOrder === expectedMessageOrder)
          )
        );
      }
    } else {
      if (DEBUG_STREAMING) console.log('[StreamingState] Keeping streaming state - content mismatch', {
        normalizedPersistedLength: normalizedPersisted.length,
        normalizedStreamingLength: normalizedStreaming.length,
        rawPersistedLength: rawPersistedContent.length,
        rawStreamingLength: rawStreamingContent.length,
      });
    }
  }, [selectedChatFromAPI?.messages, liveStreamText]);

  // Timeout fallback: keep live bubble and preserved content; do not clear UI on slow persistence
  useEffect(() => {
    if (!awaitingPersistAiRef.current) return;
    const timeoutId = setTimeout(() => {
      if (awaitingPersistAiRef.current) {
        console.warn('[ChatAdvisor] Timeout waiting for persisted message, keeping live bubble and preserved content');
        
        // Clear any retry intervals
        if ((window as any).__chatStreamRetryInterval) {
          clearInterval((window as any).__chatStreamRetryInterval);
          (window as any).__chatStreamRetryInterval = null;
        }
        
        // Do NOT clear any buffers or hide the bubble. Keep awaiting flag true so reconciliation can occur later.
      }
    }, 10000); // 10 second timeout
    
    return () => clearTimeout(timeoutId);
  }, [awaitingPersist]);

  // Ensure exactly one chat creation occurs even under concurrent triggers
  const ensureChatId = async (forceNew: boolean = false): Promise<string> => {
    if (!forceNew && selectedChatId) {
      try { console.log('[ChatAdvisor] ensureChatId: reusing selectedChatId', selectedChatId); } catch {}
      return selectedChatId;
    }
    if (createInFlightRef.current) {
      try { console.log('[ChatAdvisor] ensureChatId: awaiting in-flight creation'); } catch {}
      const id = await createInFlightRef.current;
      if (id) return id;
      throw new Error('Chat creation failed');
    }
    createInFlightRef.current = (async () => {
      try {
        try { console.log('[ChatAdvisor] ensureChatId: creating new chat', { forceNew, clientId: selectedClient?._id, tenantId: selectedTenant?._id }); } catch {}
        // ── Agent backend: createAgentThread via graphql-request ──
        // clientId is intentionally omitted when no client is
        // selected: TD_CLIENT users have no picker and the backend
        // resolver auto-derives clientId from their tenantId. Other
        // roles that DO have a picker will see a clear "clientId is
        // required" error if they hit New Chat without picking one.
        const headers = accessToken ? { Authorization: accessToken } : undefined;
        const agentResp: any = await (request as any)(
          gqlServerURL + '/graphql',
          CREATE_AGENT_THREAD_MUTATION,
          { title: "New Chat", tenantId: selectedTenant?._id || user?.tenantId || undefined, clientId: selectedClient?._id || undefined },
          headers
        );
        const chatId: string = agentResp?.createAgentThread?._id;
        if (!chatId) throw new Error('createAgentThread returned no _id');
        try { console.log('[ChatAdvisor] ensureChatId (agent): created thread', chatId); } catch {}
        setSelectedChatId(chatId);
        setShowChatInterface(true);
        autoCreatedRef.current = true;
        // Ensure the newly created chat appears and is selected in the sidebar immediately
        try {
          try { console.log('[ChatAdvisor] ensureChatId: refetching chats after create'); } catch {}
          refetchChats && (await refetchChats());
        } catch (err) {
          try { console.warn('[ChatAdvisor] ensureChatId: refetchChats failed', err); } catch {}
        }
        return chatId as string;
      } catch (e) {
        try { console.error('[ChatAdvisor] ensureChatId: creation failed', e); } catch {}
        autoCreatedRef.current = false;
        return null;
      } finally {
        try { console.log('[ChatAdvisor] ensureChatId: clearing in-flight flag'); } catch {}
        createInFlightRef.current = null;
      }
    })();
    const id = await createInFlightRef.current;
    if (!id) throw new Error('Chat creation failed');
    return id;
  };

  const handleExportPdf = async () => {
    if (!messagesScrollRef.current || isExporting) return;
    setIsExporting(true);
    try {
      const title = selectedChat?.title || "Chat Export";
      await exportChatToPdf(messagesScrollRef.current, title);
      trackExportDownloaded("pdf");
    } catch (error) {
      console.error("Failed to export PDF:", error);
      showSnackbar("Export Failed", "Could not export chat to PDF", "error");
    } finally {
      setIsExporting(false);
    }
  };

  const handleSendMessage = async () => {
    let attachmentIds = getAttachmentIds();
    const pendingIds = getPendingAttachmentIds();
    const hasAttachments = hasPendingAttachments || attachmentIds.length > 0;
    const hasContent = currentMessage.trim() || hasAttachments;

    if (hasContent && !isLoading && !isUploadingAttachment) {
      const messageContent = currentMessage.trim() || (hasAttachments ? "Please analyze the attached file(s)." : "");

      // Show loading indicator immediately when user clicks send
      // Clear any residual streaming text so the indicator shows
      setLiveStreamText(""); setLiveFinalizeArgs(null);
      setAiProgressState("starting");
      setAiProgressStatus("Starting...");
      setShowAIThinking(true);

      let chatId: string;
      try {
        try { console.log('[ChatAdvisor] handleSendMessage: ensuring chat'); } catch {}
        chatId = await ensureChatId();
        try { console.log('[ChatAdvisor] handleSendMessage: using chatId', chatId); } catch {}
        if (selectedChatId !== chatId) {
          try { console.log('[ChatAdvisor] handleSendMessage: correcting selection to', chatId); } catch {}
          setSelectedChatId(chatId);
          setShowChatInterface(true);
        }
      } catch (err) {
        // Reset loading state on error
        setAiProgressState("idle");
        setAiProgressStatus("");
        setShowAIThinking(false);
        try {
          showSnackbar(
            "Unable to start chat",
            "We couldn't create a new chat. Please try again.",
            "error",
            5000
          );
        } catch {}
        return; // Keep input on failure
      }

      // Associate any pending attachments with this chat (triggers async ingestion)
      if (pendingIds.length > 0) {
        try {
          const associatedAttachments = await associateAttachmentsWithChat(chatId);
          // Add the associated attachment IDs to our list
          const associatedIds = associatedAttachments.map((att) => att._id);
          attachmentIds = [...attachmentIds, ...associatedIds];
        } catch (err) {
          console.error('[ChatAdvisor] Failed to associate pending attachments:', err);
        }
      }

      // Build local preview URLs for image attachments
      const localPreviews: Record<string, string> = {};
      try {
        pendingAttachments.forEach((pending) => {
          if (!pending.attachment?._id) return;
          const id = pending.attachment._id;
          if (pending.file?.type?.startsWith("image/")) {
            // Only create if we don't already have a signed/local URL
            if (!attachmentUrlMap[id]) {
              const blobUrl = URL.createObjectURL(pending.file);
              localPreviews[id] = blobUrl;
              blobPreviewUrlsRef.current[id] = blobUrl;
            }
          }
        });
      } catch {}

      // Only clear input once a target chat is ensured
      setCurrentMessage("");
      try {
        await sendStreamingMessage(messageContent, chatId, attachmentIds, localPreviews);
        // Only clear attachments after we successfully start sending the message.
        // If sending fails, keep attachments so the user can retry without losing uploads.
        clearAttachments();
      } catch {
        // sendStreamingMessage already restores the input text and clears optimistic UI state.
        // Intentionally do NOT clear attachments here.
      }
    }
  };

  const sendStreamingMessage = async (
    messageContent: string,
    chatIdOverride?: string | null,
    attachmentIds?: string[],
    localAttachmentPreviews?: Record<string, string>
  ) => {
    const chatId = chatIdOverride || selectedChatId;
    if (!chatId) return;
    
    // Calculate expected messageOrder for user and AI messages
    // The backend creates: user message (lastMessageOrder + 1), then AI message (lastMessageOrder + 2)
    const currentMessages = selectedChatFromAPI?.messages || [];
    const lastMessage = currentMessages[currentMessages.length - 1];
    
    // If we're preserving a previous stream, account for it in the order calculation
    // The preserved message has awaitingPersistMessageOrderRef.current as its messageOrder
    let lastMessageOrder: number;
    if (awaitingPersistMessageOrderRef.current !== null) {
      // We're preserving a message, so use its messageOrder as the base
      // The new user message should come after the preserved message
      lastMessageOrder = awaitingPersistMessageOrderRef.current;
    } else if (lastMessage?.messageOrder != null) {
      lastMessageOrder = lastMessage.messageOrder;
    } else {
      lastMessageOrder = currentMessages.length > 0 ? currentMessages.length - 1 : -1;
    }
    
    const expectedUserMessageOrder = lastMessageOrder + 1;
    const expectedAiMessageOrder = lastMessageOrder + 2;
    
    // Store the expected messageOrder for this stream
    streamingMessageOrderRef.current = expectedAiMessageOrder;

    // If we are preserving previous stream content, clear current live buffers
    // so preserved virtual exclusively represents the previous response
    setLiveStreamText(""); setLiveFinalizeArgs(null);
    setLiveThinkingList([]);
    setCurrentToolCalls([]);
    setActiveToolCalls(new Set());
    setStreamingCode(null);

    const optimisticUserMessage: UIChatMessage = {
      id: `optimistic-${Date.now()}`,
      content: messageContent,
      sender: MessageSender.USER,
      timestamp: new Date().toISOString(),
      // Assign messageOrder so it sorts correctly - user message comes after last message
      messageOrder: expectedUserMessageOrder,
      attachmentIds: attachmentIds?.length ? attachmentIds : undefined,
    };
    setOptimisticMessages((prev) => [...prev, optimisticUserMessage]);

    // Seed preview URLs immediately so images show during streaming (before signed URL fetch)
    if (localAttachmentPreviews && Object.keys(localAttachmentPreviews).length > 0) {
      setAttachmentUrlMap((prev) => ({ ...prev, ...localAttachmentPreviews }));
    }
    // Ensure any previous subscription is cleaned up to avoid duplicate events
    if (wsSubDisposeRef.current) {
      try { wsSubDisposeRef.current(); } catch {}
      wsSubDisposeRef.current = null;
    }
    setShowAIThinking(true);
    // Initialize progress indicator state
    setAiProgressState("starting");
    setAiProgressStatus("Starting...");

    try {
      const headers = accessToken ? { Authorization: accessToken } : undefined;

      // Snapshot the title at send-time so the post-`done` poll can detect
      // the server-side auto-rename (agent renames after `done` is emitted).
      const titleAtSend: string =
        (chatsData?.data as any[] | undefined)?.find?.((c: any) => c._id === chatId)?.title ?? '';

      // Agent-chat path: sendAgentMessage → agentChatStream
      const agentResp: any = await (request as any)(
        gqlServerURL + '/graphql',
        SEND_AGENT_MESSAGE_MUTATION,
        { chatId, input: { content: messageContent, attachmentIds: attachmentIds?.length ? attachmentIds : undefined } },
        headers
      );
      const streamId: string | undefined = agentResp?.sendAgentMessage?.streamId;
      if (!streamId) throw new Error('sendAgentMessage returned no streamId');
      console.log('[AgentStreaming] sendAgentMessage response streamId=', streamId);
      // Anchor the streaming bubble to the slot where the AI message will land.
      // User message just got persisted at order N; AI message will land at N+1.
      try {
        const currentMsgs = (selectedChatFromAPI?.messages || []) as { messageOrder?: number }[]
        const maxOrder = currentMsgs.reduce((m, x) => Math.max(m, x.messageOrder ?? -1), -1)
        streamingMessageOrderRef.current = maxOrder + 2  // +1 for just-persisted USER, +1 for upcoming AI
      } catch {
        streamingMessageOrderRef.current = 999
      }

      setShowAIThinking(true);
      console.log('[Streaming] Subscribing to agentChatStream with streamId=', streamId);
      // Prior subscription was already disposed up top before the
      // mutation fired (see "Ensure any previous subscription is
      // cleaned up" guard) — no second cleanup needed here.
      currentStreamIdRef.current = streamId;
      // replayFirst:false — we just kicked off this stream, the
      // buffer is empty, no point round-tripping the replay query.
      const dispose = stream.subscribe(
        streamId,
        buildAgentStreamHandlers(chatId, titleAtSend, streamId),
        { replayFirst: false }
      );
      wsSubDisposeRef.current = dispose;
    } catch (error) {
      setShowAIThinking(false);
      setOptimisticMessages([]);
      setCurrentMessage(messageContent);
      throw error;
    }
  };

  // Track if user is selecting text or has scrolled up
  const userScrolledUpRef = useRef(false);
  const isSelectingTextRef = useRef(false);
  const selectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-scroll messages container on new tokens or new messages
  // Only auto-scroll if user is near bottom and not selecting text
  useEffect(() => {
    // Double-check selection state before scrolling
    const selection = window.getSelection();
    const hasActiveSelection = selection && selection.toString().length > 0;
    
    if (messagesScrollRef.current && !isSelectingTextRef.current && !hasActiveSelection) {
      try {
        const el = messagesScrollRef.current;
        const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100; // Within 100px of bottom
        
        // Only auto-scroll if user is near bottom or hasn't scrolled up
        if (isNearBottom || !userScrolledUpRef.current) {
          el.scrollTop = el.scrollHeight;
          userScrolledUpRef.current = false; // Reset flag after auto-scrolling
        }
      } catch {}
    }
  }, [liveStreamText, liveThinkingList, currentToolCalls.length, selectedChatFromAPI?.messages?.length]);

  // Track user scroll position and text selection to prevent auto-scroll interference
  useEffect(() => {
    const el = messagesScrollRef.current;
    if (!el) return;

    const handleScroll = () => {
      const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
      userScrolledUpRef.current = !isNearBottom;
    };

    // Simple selection tracking - just check if there's a selection
    const handleSelectionChange = () => {
      try {
        const selection = window.getSelection();
        isSelectingTextRef.current = !!(selection && selection.toString().length > 0);
      } catch {
        // Ignore errors
      }
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('selectionchange', handleSelectionChange);

    return () => {
      el.removeEventListener('scroll', handleScroll);
      document.removeEventListener('selectionchange', handleSelectionChange);
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
    };
  }, []);
  
  // Log render order once per message list change (high-signal, low-noise)
  useEffect(() => {
    const seq = (selectedChat?.messages || []).map((m: any) => ({
      id: m.id,
      sender: m.sender,
      order: m.messageOrder,
      live: m._isLiveStreaming ? true : undefined,
      preserved: m._isPreservedStreaming ? true : undefined,
    }));
    logOrder('RenderMessages', seq);
  }, [selectedChat?.messages]);

  // Generative UI: intercept UI prompts encoded in AI messages (simple JSON fence detection)
  const tryParseGenerativeUISpec = (content: string): UIChoiceSpec | null => {
    const attemptParse = (raw: string): UIChoiceSpec | null => {
      try {
        const parsed = JSON.parse(raw);
        if (parsed?.type === 'choice' && parsed?.id && parsed?.question && Array.isArray(parsed?.options)) {
          return parsed as UIChoiceSpec;
        }
      } catch {}
      return null;
    };

    // 1) Prefer fenced block ```ui { json }```
    const fenced = content.match(/```ui\s*([\s\S]*?)```/i);
    if (fenced) {
      const raw = fenced[1].trim();
      let parsed = attemptParse(raw);
      if (!parsed) {
        // Heuristic repair for minor streaming artifacts
        let repaired = raw;
        // a) Keep only the substring between the first '{' and the last '}'
        const firstBrace = repaired.indexOf('{');
        const lastBrace = repaired.lastIndexOf('}');
        if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
          repaired = repaired.slice(firstBrace, lastBrace + 1);
        }
        // b) Remove stray alpha tokens that appear between a closing quote and comma (e.g., '" A,')
        repaired = repaired.replace(/"\s+[A-Za-z]\w*\s*,/g, '",');
        // c) Remove trailing commas before object/array closers
        repaired = repaired.replace(/,(\s*[}\]])/g, '$1');
        // d) Normalize smart quotes
        repaired = repaired.replace(/[“”]/g, '"');
        parsed = attemptParse(repaired);
        if (parsed) return parsed;
      } else {
        return parsed;
      }
    }
    // 2) Fallback: inline JSON block that looks like a UI spec
    const inline = content.match(/\{[\s\S]*?"type"\s*:\s*"choice"[\s\S]*?"options"\s*:\s*\[[\s\S]*?\][\s\S]*?\}/);
    if (inline) {
      const raw = inline[0];
      let parsed = attemptParse(raw);
      if (!parsed) {
        let repaired = raw;
        const firstBrace = repaired.indexOf('{');
        const lastBrace = repaired.lastIndexOf('}');
        if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
          repaired = repaired.slice(firstBrace, lastBrace + 1);
        }
        repaired = repaired.replace(/"\s+[A-Za-z]\w*\s*,/g, '",');
        repaired = repaired.replace(/,(\s*[}\]])/g, '$1');
        repaired = repaired.replace(/[“”]/g, '"');
        parsed = attemptParse(repaired);
        if (parsed) return parsed;
      } else {
        return parsed;
      }
    }
    return null;
  };

  // stripUiSpecFromContent defined earlier (hoisted)


  const handleGenerativeUISubmit = async (payload: { id: string; value: string }) => {
    // If a stream is active, preserve its content so it remains visible while we start the new one
    if (showAIThinking && wsSubDisposeRef.current && streamingMessageOrderRef.current !== null) {
      // Snapshot the current streaming content and anchor its messageOrder
      awaitingPersistMessageOrderRef.current = streamingMessageOrderRef.current;
      preservedStreamingContentRef.current = {
        text: liveStreamText,
        thinkingList: [...liveThinkingList], // full cumulative thinking up to this point
        toolCalls: [...currentToolCalls],
      };
      awaitingPersistAiRef.current = true;
      setAwaitingPersist(true);
      // Reset current stream order to avoid inserting a live virtual at the previous order
      streamingMessageOrderRef.current = null;
      
      // Cancel the WebSocket subscription but keep the streaming content visible
      try { wsSubDisposeRef.current(); } catch {}
      wsSubDisposeRef.current = null;
      
      // Keep live state visible for preserved content; the new stream will set its own messageOrder
      setShowAIThinking(false);
      // Wipe current live buffers for the next stream so old thoughts don't leak into new stream
      try {
        setLiveStreamText(""); setLiveFinalizeArgs(null);
        setLiveThinkingList([]);
        setCurrentToolCalls([]);
        setActiveToolCalls(new Set());
      } catch {}
    }
    // Auto-send the selected value as a user message
    try {
      await sendStreamingMessage(payload.value);
    } catch {
      // sendStreamingMessage is responsible for resetting UI state on failure.
    }
  };

  // Helper to check if a message already yields a final code (avoid showing UI on final answer)
  const resolveFinalCodeForMessage = (_messageId: string, content: string): string | undefined => {
    return extractFirstCodeAfterFinal(content);
  };

  // Show at most one unresolved UI prompt: last AI message with UI spec and without a final code, and without a user reply after it
  const lastUiIdx = useMemo(() => {
    if (!selectedChat?.messages) return -1;
    let lastIdx = -1;
    selectedChat.messages.forEach((m, idx) => {
      if (m.sender !== MessageSender.AI) return;
      // Two ways a message can carry a UI choice prompt:
      // 1. New finalize-tool path: m.finalizeArgs.kind === 'clarifying_question'
      //    with m.finalizeArgs.question set. The renderer reads buttons
      //    directly from there (see lines ~2547). This is the path we want
      //    on every new message — the old fenced ```ui block format isn't
      //    emitted by the finalize architecture anymore.
      // 2. Back-compat path: a fenced ```ui block in m.content for
      //    older messages persisted before the finalize refactor.
      const hasFinalizeQuestion =
        m.finalizeArgs?.kind === 'clarifying_question' &&
        !!m.finalizeArgs?.question?.options?.length;
      const legacySpec = !hasFinalizeQuestion ? tryParseGenerativeUISpec(m.content) : null;
      if (!hasFinalizeQuestion && !legacySpec) return;
      const hasFinal = !!resolveFinalCodeForMessage(m.id, m.content);
      if (hasFinal) return;
      lastIdx = idx;
    });
    return lastIdx;
  }, [selectedChat?.messages, selectedChatFromAPI]);

  const isAnsweredAfter = (startIdx: number): boolean => {
    if (!selectedChat?.messages || startIdx < 0) return true;
    for (let i = startIdx + 1; i < selectedChat.messages.length; i++) {
      if (selectedChat.messages[i].sender === MessageSender.USER) return true;
    }
    return false;
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  // Handle file attachment selection
  const handleFileSelect = async (file: File) => {
    // Always upload immediately - uses pending flow if no chat exists
    await uploadAttachment(file, selectedChatId || undefined);
  };

  // Handle removing an attachment
  const handleRemoveAttachment = async (attachmentId: string) => {
    // removeAttachment handles state removal even without chatId, so we can remove queued attachments before chat creation
    await removeAttachment(attachmentId, selectedChatId || undefined);
  };

  const handleNewChat = async () => {
    try {
      clearAttachments();
      try { console.log('[ChatAdvisor] handleNewChat: creating explicitly via ensureChatId(forceNew=true)'); } catch {}
      const id = await ensureChatId(true);
      if (id) {
        if (selectedChatId !== id) {
          setSelectedChatId(id);
        }
        setShowChatInterface(true);
        autoCreatedRef.current = true;
      }
      try {
        try { console.log('[ChatAdvisor] handleNewChat: refetching chats'); } catch {}
        refetchChats && (await refetchChats());
      } catch (err) {
        try { console.warn('[ChatAdvisor] handleNewChat: refetch failed', err); } catch {}
      }
    } catch (error) {
      // Handle error silently
    }
  };

  const handleChatSelect = (chatId: string) => {
    if (selectedChatId === chatId) {
      try { refetchSelectedChat && refetchSelectedChat(); } catch {}
    } else {
      // Pending attachments are chat-scoped; switching chats should reset them.
      clearAttachments();
      setSelectedChatId(chatId);
      setShowChatInterface(true);
    }
    trackChatOpened(chatId);
  };

  const handleExampleMessageClick = async (message: string) => {
    // Show loading indicator immediately when preset is clicked
    setLiveStreamText(""); setLiveFinalizeArgs(null);
    setAiProgressState("starting");
    setAiProgressStatus("Starting...");
    setShowAIThinking(true);

    try {
      try { console.log('[ChatAdvisor] handleExampleMessageClick: preset clicked; ensuring chat'); } catch {}
      const chatId = await ensureChatId();
      try { console.log('[ChatAdvisor] handleExampleMessageClick: using chatId', chatId); } catch {}
      if (selectedChatId !== chatId) {
        try { console.log('[ChatAdvisor] handleExampleMessageClick: correcting selection to', chatId); } catch {}
        setSelectedChatId(chatId);
        setShowChatInterface(true);
      }
      if (chatId) {
        // Calculate expected messageOrder for user and AI messages
        // Backend creates: user (lastOrder + 1), then AI (lastOrder + 2)
        const currentMessages = selectedChatFromAPI?.messages || [];
    const lastMessage = currentMessages[currentMessages.length - 1];
    
    // Determine the last known order considering any preserved previous stream
    let lastOrder: number;
    if (awaitingPersistMessageOrderRef.current !== null) {
      lastOrder = awaitingPersistMessageOrderRef.current!;
    } else if (lastMessage?.messageOrder != null) {
      lastOrder = lastMessage.messageOrder;
    } else {
      lastOrder = currentMessages.length > 0 ? currentMessages.length - 1 : -1;
    }
        const expectedUserMessageOrder = lastOrder + 1;
        const expectedAiMessageOrder = lastOrder + 2;
        
        // Store the expected messageOrder for this stream
        streamingMessageOrderRef.current = expectedAiMessageOrder;
        
        const optimisticUserMessage: UIChatMessage = {
          id: `optimistic-${Date.now()}`,
          content: message,
          sender: MessageSender.USER,
          timestamp: new Date().toISOString(),
          messageOrder: expectedUserMessageOrder,
        };
        
        setOptimisticMessages(prev => [...prev, optimisticUserMessage]);
        setShowAIThinking(true);

        try {
          // Streaming for example message with auth header
          const headers = accessToken ? { Authorization: accessToken } : undefined;

          // Snapshot the title at send-time so the post-`done` poll can detect
          // the server-side auto-rename (agent renames after `done` is emitted).
          const titleAtSend: string =
            (chatsData?.data as any[] | undefined)?.find?.((c: any) => c._id === chatId)?.title ?? '';

          const agentResp: any = await (request as any)(
            gqlServerURL + '/graphql',
            SEND_AGENT_MESSAGE_MUTATION,
            { chatId, input: { content: message } },
            headers
          );
          const streamId: string | undefined = agentResp?.sendAgentMessage?.streamId;
          if (!streamId) throw new Error('sendAgentMessage returned no streamId');
          // eslint-disable-next-line no-console
          console.log('[AgentStreaming] sendAgentMessage response streamId=', streamId);
          // Anchor the streaming bubble to the slot where the AI message will land.
          // User message just got persisted at order N; AI message will land at N+1.
          try {
            const currentMsgs = (selectedChatFromAPI?.messages || []) as { messageOrder?: number }[]
            const maxOrder = currentMsgs.reduce((m, x) => Math.max(m, x.messageOrder ?? -1), -1)
            streamingMessageOrderRef.current = maxOrder + 2  // +1 for just-persisted USER, +1 for upcoming AI
          } catch {
            streamingMessageOrderRef.current = 999
          }

          setShowAIThinking(true);
          // eslint-disable-next-line no-console
          console.log('[Streaming] Subscribing to agentChatStream with streamId=', streamId);
          try { wsSubDisposeRef.current?.(); } catch {}
          wsSubDisposeRef.current = null;
          currentStreamIdRef.current = streamId;
          const dispose = stream.subscribe(
            streamId,
            buildAgentStreamHandlers(chatId, titleAtSend, streamId),
            { replayFirst: false }
          );
          wsSubDisposeRef.current = dispose;

        } catch (error) {
          setShowAIThinking(false);
          setOptimisticMessages([]);
          throw error;
        }
      }
    } catch (error) {
      // Reset loading state on error
      setLiveStreamText(""); setLiveFinalizeArgs(null);
      setAiProgressState("idle");
      setAiProgressStatus("");
      setShowAIThinking(false);
    }
  };

  // Render-tree stability: once the user has picked (or just created) a
  // chat we always render the conversation shell, never the new-chat
  // splash. Without `Boolean(selectedChatId)` here, a refetch race
  // could briefly null out selectedChat while optimisticMessages were
  // also being cleared, flipping this gate to false and unmounting the
  // entire chat — what surfaced as "blank screen on send."
  // selectedChatFallback (set when selectedChatId exists but the API
  // hasn't returned yet) was supposed to cover this; it doesn't catch
  // every race, so we anchor on the id directly as a second line.
  // Use `firstMessageAt` as the persisted "this chat has begun" signal —
  // a stable backend field that doesn't flicker during refetches. A bare
  // selectedChatId truthy gate was hiding NewChatView the moment a fresh
  // chat was created (handleNewChat sets selectedChatId immediately).
  // The send-in-flight branches (isLoading, liveStreamText, liveThinkingList)
  // cover the persist→refetch race the old comment was guarding against.
  const hasMessages = Boolean(
    selectedChatData?.firstMessageAt ||
    (selectedChat && selectedChat.messages.length > 0) ||
    optimisticMessages.length > 0 ||
    isLoading ||
    !!liveStreamText ||
    liveThinkingList.length > 0
  );

  // Hoisted function to avoid temporal dead zone errors
  function extractFirstCodeAfterFinal(text: string): string | undefined {
    if (!text) return undefined;
    // Match either:
    // (a) "final"/"complete" near a code-context word (hts/htsus/code/classification)
    //     — covers "Final code:", "Final classification:", "Final HTS code:", etc.
    // (b) a bare "HTS Code"/"HTS Classification"/"HTSUS Code"/"HTSUS Classification"
    //     phrase — covers the agent's habit of writing answer headers like
    //     "HTS Classification: 6205.90.4040" without the "final" prefix.
    // The 10-digit code can be in 4-2-2-2 dotted, 4-2-4 dotted, or undotted form.
    const markerMatch = text.match(
      /\b(?:(?:final|complete)\b[^a-zA-Z]{0,20}\b(?:hts|htsus|code|classification)\b|hts(?:us)?\s+(?:code|classification))\b/i
    );
    const markerIdx = markerMatch ? (markerMatch.index ?? -1) : -1;
    if (markerIdx === -1) return undefined;
    const after = text.slice(markerIdx);
    // Prefer 4-2-2-2 dotted form (e.g., 7013.49.20.90)
    const dottedFour = after.match(/\b(\d{4})\.(\d{2})\.(\d{2})\.(\d{2})\b/);
    if (dottedFour) return `${dottedFour[1]}${dottedFour[2]}${dottedFour[3]}${dottedFour[4]}`;
    const dotted = after.match(/\b(\d{4})\.(\d{2})\.(\d{4})\b/);
    if (dotted) return `${dotted[1]}${dotted[2]}${dotted[3]}`;
    const flat = after.match(/\b(\d{10})\b/);
    if (flat) return flat[1];
    return undefined;
  }

  // Scan live stream text for a code when the stream pauses (0.5s debounce)
  useEffect(() => {
    if (!liveStreamText) return;
    
    const timer = setTimeout(() => {
      const code = extractFirstCodeAfterFinal(liveStreamText);
      if (code) {
        setStreamingCode(code);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [liveStreamText]);

  // If the currently selected chat is deleted, clear selection and show the blank template
  useEffect(() => {
    if (!selectedChatId) return;
    const exists = chatSessions.some((c) => c.id === selectedChatId);
    if (exists) {
      deletedSelectionHandledRef.current = false;
      // If the newly created chat is now present in the list, drop the creation guard
      autoCreatedRef.current = false;
      return;
    }
    // If we just created a chat or a creation is in-flight, don't treat missing-from-list as deletion
    if (createInFlightRef.current || autoCreatedRef.current) return;
    // Clear selection so the new-chat suggestions screen is visible
    if (!deletedSelectionHandledRef.current) {
      deletedSelectionHandledRef.current = true;
      setSelectedChatId(null);
      setShowChatInterface(false);
      // allow handling future deletions
      setTimeout(() => { deletedSelectionHandledRef.current = false; }, 50);
    }
  }, [chatSessions, selectedChatId, selectedClient]);

  

  if (chatsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (chatsError) {
    const errorMessage = "Failed to load chats. Please try refreshing the page.";
    let errorDetails = "";
    
    if ('status' in chatsError) {
      errorDetails += `Status: ${chatsError.status}. `;
    }
    if ('data' in chatsError && chatsError.data) {
      errorDetails += `Data: ${JSON.stringify(chatsError.data)}. `;
    }
    if ('error' in chatsError && chatsError.error) {
      errorDetails += `Error: ${chatsError.error}. `;
    }
    if ('message' in chatsError && chatsError.message) {
      errorDetails += `Message: ${chatsError.message}. `;
    }
    
    return (
      <Box p={3}>
        <Alert severity="error">
          <div>{errorMessage}</div>
          {import.meta.env.DEV && errorDetails && (
            <div style={{ fontSize: tokens.fontSize.base, marginTop: '8px', opacity: 0.8 }}>
              Debug info: {errorDetails}
            </div>
          )}
        </Alert>
      </Box>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: tokens.bg.surface,
        height: "100vh",
        minHeight: 0,
        overflowX: "hidden",
        overflowY: "auto",
        borderRadius: tokens.radius.lg,
      }}
    >
      
      {/* Header for cramped/mobile views */}
      {useDrawerSidebar && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: "1px solid #E6E7E7",
            backgroundColor: tokens.bg.surface,
          }}
        >
          <IconButton
            onClick={() => setMobileDrawerOpen(true)}
            sx={{
              p: 1,
              color: "#031112",
            }}
          >
            <FiMenu size={22} />
          </IconButton>
          <MuiTypography
            sx={{
              fontWeight: tokens.fontWeight.bold,
              fontSize: tokens.fontSize.xl,
              color: "#031112",
              flex: 1,
              textAlign: "center",
            }}
          >
            {selectedChat?.title || "Riffs Intelligence"}
          </MuiTypography>
          <IconButton
            onClick={handleNewChat}
            disabled={createChatLoading}
            sx={{
              p: 1,
              color: tokens.brand.primary,
            }}
          >
            <FiPlus size={22} />
          </IconButton>
        </Box>
      )}

      {/* Drawer Sidebar for cramped/mobile views */}
      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: "85%",
            maxWidth: 320,
          },
        }}
      >
        <Sidebar
          isOpen={true}
          width="100%"
          groupedSessions={groupedSidebarSessions}
          selectedChatId={selectedChatId}
          onChatSelect={(id) => {
            handleChatSelect(id);
            setMobileDrawerOpen(false);
          }}
          onNewChat={() => {
            handleNewChat();
            setMobileDrawerOpen(false);
          }}
          isLoading={createChatLoading}
        />
      </Drawer>

      <div
        style={{
          display: "flex",
          marginTop: 0,
          flex: 1,
          minHeight: 0, // Allow flex item to shrink
        }}
      >
        {/* Desktop Sidebar - hidden on cramped/mobile views */}
        {!useDrawerSidebar && (
          <Sidebar
            isOpen={isSidebarOpen}
            width={sidebarWidth}
            groupedSessions={groupedSidebarSessions}
            selectedChatId={selectedChatId}
            onChatSelect={handleChatSelect}
            onNewChat={handleNewChat}
            isLoading={createChatLoading}
          />
        )}

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            width: "auto",
            minHeight: 0,
            position: 'relative',
          }}
        >
          <div
            ref={topOverlayRef}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: 0,
              marginTop: 0,
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1,
              justifyContent: "flex-end",
              pointerEvents: "none",
            }}
          >
            <div style={{ pointerEvents: "auto", padding: 16, display: 'flex', gap: 8 }}>
              <Tooltip title="Export Chat to PDF" arrow>
                <span>
                  <IconButton 
                    size="small" 
                    sx={{ p: 0, color: "#8E9494" }} 
                    onClick={handleExportPdf}
                    disabled={isExporting || !hasMessages}
                    aria-label="Export PDF"
                  >
                    {isExporting ? <CircularProgress size={16} color="inherit" /> : <FiDownload fontSize={tokens.fontSize.xl} />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
              placement="right"
              arrow
              title={
                <Box sx={{ maxWidth: 440, p: 2 }}>
                  <MuiTypography variant="body2" color="inherit" sx={{ fontWeight: 800, mb: 1 }}>
                    AI Trade Assistance Disclaimer
                  </MuiTypography>
                  <MuiTypography variant="body2" color="inherit" sx={{ lineHeight: 1.5, mb: 1.25 }}>
                    Responses are generated by AI based on your inputs and available data. They are intended for informational purposes only and do not constitute legal or compliance advice.
                  </MuiTypography>
                  <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 1.25 } }}>
                    <li>
                      <MuiTypography variant="body2" color="inherit" sx={{ lineHeight: 1.5 }}>
                        Always validate HTS classifications, duty rates, and rulings with official sources.
                      </MuiTypography>
                    </li>
                    <li>
                      <MuiTypography variant="body2" color="inherit" sx={{ lineHeight: 1.5 }}>
                        Trade regulations change frequently; this chat attempts to use the latest data, not historical data.
                      </MuiTypography>
                    </li>
                    <li>
                      <MuiTypography variant="body2" color="inherit" sx={{ lineHeight: 1.5 }}>
                        Engage a licensed customs broker or legal advisor for binding determinations.
                      </MuiTypography>
                    </li>
                  </Box>
                  <MuiTypography variant="body2" color="inherit" sx={{ lineHeight: 1.4, mt: 1, opacity: 0.85 }}>
                    By using this feature, you acknowledge these limitations and accept responsibility for final decisions.
                  </MuiTypography>
                </Box>
              }
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: { offset: [0, -8] },
                    },
                  ],
                },
                tooltip: {
                  sx: {
                    bgcolor: '#0F1E2E',
                    color: 'white',
                    fontSize: '0.875rem',
                    borderRadius: tokens.radius.lg,
                    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.35)',
                    '& .MuiTooltip-arrow': {
                      color: '#0F1E2E',
                    },
                  },
                },
              }}
            >
              <IconButton size="small" sx={{ p: 0, color: "#8E9494" }} aria-label="AI disclaimer">
                <FiInfo fontSize={tokens.fontSize.xl} />
              </IconButton>
            </Tooltip>
            </div>
          </div>

          {hasMessages ? (
            <ChatErrorBoundary resetKey={selectedChatId}>
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0, // Allow flex item to shrink
              }}
            >
              
              
              <div
                className="chat-messages-container"
                style={{
                  flex: 1,
                  boxSizing: 'border-box',
                  paddingTop: (Math.max(overlayTopPx || 0, isSuperCramped ? 36 : 48)) + 12,
                  paddingBottom: (Math.max(overlayBottomPx || 0, isSuperCramped ? 56 : 80)) + ((isLoading || !!liveStreamText) ? 16 : 0) + 100, // Increased bottom padding
                  paddingLeft: 32,
                  paddingRight: 32,
                  overflowY: "auto",
                  // Explicitly enable text selection
                  userSelect: 'text',
                  WebkitUserSelect: 'text',
                }}
                ref={messagesScrollRef}
              >
                {selectedChat?.messages.map((message, idx) => {
                  
              // Render active live streaming content as an in-line virtual message
              if ((message as any)._isLiveStreaming) {
                // Key by messageOrder so the persisted AI message reuses this
                // same DOM container at swap time — prevents the blank flash
                // that happened when the live `live-streaming-N` node was
                // unmounted and the persisted `<mongo-id>` node was mounted
                // in its place.
                const stableKey = message.messageOrder != null
                  ? `ai-${message.messageOrder}`
                  : message.id;
                return (
                  <div key={stableKey}>
                    {/* Show thinking/tool calls FIRST */}
                    {((liveThinkingList.length > 0) || currentToolCalls.length > 0) && (
                      <AIProgressIndicator
                        state={aiProgressState}
                        statusText={aiProgressStatus}
                        thinkingItems={liveThinkingList}
                        activeTools={currentToolCalls.map(tc => ({
                          name: tc.name,
                          isActive: activeToolCalls.has(tc.name)
                        }))}
                      />
                    )}
                    {/* Then show the actual response text */}
                    {(() => {
                      // Prefer the structured finalize envelope when it
                      // arrives; fall back to the legacy markdown-parsing
                      // path for any streams that haven't emitted finalize
                      // yet (or older deploys).
                      const spec: UIChoiceSpec | null =
                        liveFinalizeArgs?.kind === 'clarifying_question' && liveFinalizeArgs.question
                          ? { type: 'choice', ...liveFinalizeArgs.question }
                          : tryParseGenerativeUISpec(liveStreamText || '');
                      const displayLiveText =
                        liveFinalizeArgs?.analysis_markdown
                        ?? stripUiSpecFromContent(liveStreamText || '');
                      const hasDisplayContent = displayLiveText.trim().length > 0;
                      const liveHtsCode = liveFinalizeArgs?.final_hts_code
                        ? liveFinalizeArgs.final_hts_code.replace(/\D/g, "").slice(0, 10)
                        : streamingCode;
                      return (
                        <>
                          {hasDisplayContent && (
                            <ChatBubble message={displayLiveText} sender={MessageSender.AI} />
                          )}
                          {spec && (
                            <GenerativeUIBlock spec={spec} onSubmit={handleGenerativeUISubmit} />
                          )}
                          {liveHtsCode && (
                            <div style={{ marginTop: 12, marginBottom: 4 }}>
                              <InlineDutyCalculator
                                htsCode={liveHtsCode}
                                displayCode={`${liveHtsCode.slice(0,4)}.${liveHtsCode.slice(4,6)}.${liveHtsCode.slice(6,8)}.${liveHtsCode.slice(8,10)}`}
                                chatId={selectedChatId || undefined}
                                initialState={message.messageOrder != null ? dutyCalculatorStates[String(message.messageOrder)] : undefined}
                                onStateChange={(newState) => {
                                  if (message.messageOrder != null) {
                                    setDutyCalculatorStates(prev => ({ ...prev, [String(message.messageOrder)]: newState }));
                                  }
                                }}
                              />
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                );
              }
                  
                  // Structured envelope from the agent's finalize tool.
                  // Newer messages carry this; old messages fall back to
                  // markdown parsing for the HTS code and UI spec.
                  const finalizeArgs = message.finalizeArgs;

                  let code: string | undefined;
                  if (finalizeArgs?.final_hts_code) {
                    code = finalizeArgs.final_hts_code.replace(/\D/g, "").slice(0, 10);
                  } else {
                    const contentNoUI = stripUiSpecFromContent(message.content);
                    code = extractFirstCodeAfterFinal(contentNoUI);
                  }

                  // Clarifying-question UI block: prefer finalizeArgs.question.
                  const uiSpec: UIChoiceSpec | null =
                    (idx === lastUiIdx && !isAnsweredAfter(idx))
                      ? (finalizeArgs?.kind === 'clarifying_question' && finalizeArgs.question
                          ? { type: 'choice', ...finalizeArgs.question }
                          : tryParseGenerativeUISpec(message.content))
                      : null;

                  // Markdown body: prefer finalizeArgs.analysis_markdown.
                  // The strip-ui-spec helper stays as a fallback for old
                  // messages whose markdown still has the fenced ```ui block.
                  const displayContent = finalizeArgs?.analysis_markdown
                    ?? stripUiSpecFromContent(message.content);
                  
                  // Debug logging for UI spec issues (disabled in production)
                  // Set DEBUG_STREAMING = true at top of file to enable
                  const hasThinkingStates = message.sender === MessageSender.AI && (message.llmMetadata?.thinking?.length ?? 0) > 0;
                  const hasToolCalls = message.sender === MessageSender.AI && (message.llmMetadata?.toolCalls?.length ?? 0) > 0;
                  
                  // Key by messageOrder for AI messages so the persisted node
                  // reuses the same container the live-virtual node was using
                  // (see `_isLiveStreaming` branch above). Without this, swap
                  // unmounts the live node and mounts the persisted node,
                  // causing a brief blank frame between renders.
                  const stableKey = message.sender === MessageSender.AI && message.messageOrder != null
                    ? `ai-${message.messageOrder}`
                    : message.id;
                  return (
                    <div key={stableKey} className="chat-message-container">
                      {/* Show thinking states for AI messages if they exist */}
                      {message.sender === MessageSender.AI && (hasThinkingStates || hasToolCalls) && (
                        <AIProgressIndicator
                          state="complete"
                          isSaved={true}
                          thinkingItems={message.llmMetadata?.thinking || []}
                          activeTools={message.llmMetadata?.toolCalls?.map((tc: any) => ({
                            name: convertToolName(tc.name || tc.toolName),
                            isActive: false
                          })) || []}
                        />
                      )}
                      {/* Render message attachments (images/documents) inline with the message */}
                      {message.attachmentIds && message.attachmentIds.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: message.sender === MessageSender.USER ? "flex-end" : "flex-start",
                            width: "100%",
                            marginBottom: 10,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 10,
                              maxWidth: message.sender === MessageSender.USER ? "70%" : "100%",
                              justifyContent: message.sender === MessageSender.USER ? "flex-end" : "flex-start",
                            }}
                          >
                          {message.attachmentIds.map((attachmentId) => {
                            const att = attachmentsById[attachmentId];
                            if (!att) {
                              return (
                                <div
                                  key={attachmentId}
                                  style={{
                                    fontSize: tokens.fontSize.base,
                                    color: "#687071",
                                    border: "1px solid #E6E7E7",
                                    borderRadius: tokens.radius.md,
                                    padding: "6px 10px",
                                  }}
                                >
                                  Attachment: {attachmentId}
                                </div>
                              );
                            }

                            const url = attachmentUrlMap[attachmentId];
                            const isImage = att.mimeType?.startsWith("image/");

                            if (isImage && url) {
                              return (
                                <div
                                  key={attachmentId}
                                  style={{
                                    border: "1px solid #E6E7E7",
                                    borderRadius: tokens.radius.lg,
                                    overflow: "hidden",
                                    width: isMobileWidth ? '100%' : 220,
                                    maxWidth: isMobileWidth ? 200 : 220,
                                    cursor: "pointer",
                                    background: tokens.bg.surface,
                                  }}
                                  onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                                  title={att.filename}
                                >
                                  <img
                                    src={url}
                                    alt={att.filename}
                                    style={{
                                      width: "100%",
                                      height: isMobileWidth ? 'auto' : 160,
                                      aspectRatio: isMobileWidth ? '16/9' : undefined,
                                      objectFit: "cover",
                                      display: "block"
                                    }}
                                  />
                                  <div style={{ padding: "8px 10px", fontSize: tokens.fontSize.base, color: "#031112" }}>
                                    <div style={{ fontWeight: tokens.fontWeight.bold, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {att.filename}
                                    </div>
                                    <div style={{ color: "#687071", marginTop: 2 }}>
                                      {att.status}
                                    </div>
                                  </div>
                                </div>
                              );
                            }

                            return (
                              <div
                                key={attachmentId}
                                style={{
                                  border: "1px solid #E6E7E7",
                                  borderRadius: tokens.radius.lg,
                                  padding: "8px 10px",
                                  background: tokens.bg.surface,
                                  cursor: "pointer",
                                  minWidth: isMobileWidth ? 160 : 220,
                                  maxWidth: isMobileWidth ? 200 : 280,
                                  flex: isMobileWidth ? '1 1 auto' : undefined,
                                }}
                                onClick={async () => {
                                  try {
                                    const res = await getAccessUrl({ id: attachmentId }).unwrap();
                                    window.open(res.signedUrl, "_blank", "noopener,noreferrer");
                                  } catch {
                                    // ignore
                                  }
                                }}
                                title={att.filename}
                              >
                                <div style={{ fontSize: tokens.fontSize.base, fontWeight: 650, color: "#031112", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {att.filename}
                                </div>
                                <div style={{ fontSize: tokens.fontSize.base, color: "#687071", marginTop: 4 }}>
                                  {att.mimeType}
                                </div>
                              </div>
                            );
                          })}
                          </div>
                        </div>
                      )}
                      <ChatBubble message={displayContent} sender={message.sender} />
                      <MessageActions
                        content={displayContent}
                        messageId={message.id}
                        sender={message.sender}
                        showAlways={isMobileWidth}
                      />
                      {(idx === lastUiIdx && uiSpec) && (
                        <GenerativeUIBlock spec={uiSpec} onSubmit={handleGenerativeUISubmit} />
                      )}
                      {code && (
                        <div style={{ marginTop: 12, marginBottom: 4 }}>
                          <InlineDutyCalculator
                            htsCode={code}
                            displayCode={`${code.slice(0,4)}.${code.slice(4,6)}.${code.slice(6,8)}.${code.slice(8,10)}`}
                            chatId={selectedChatId || undefined}
                            initialState={message.messageOrder != null ? dutyCalculatorStates[String(message.messageOrder)] : undefined}
                            onStateChange={(newState) => {
                              if (message.messageOrder != null) {
                                setDutyCalculatorStates(prev => ({ ...prev, [String(message.messageOrder)]: newState }));
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
                 {( (() => {
                       // Show streaming bubble if:
                       // 1. We're actively loading OR awaiting persistence
                       // 2. We have streaming text OR thinking items OR tool calls (from current or preserved stream)
                       // 3. The streaming content doesn't match the last persisted AI message
                       //
                       // Source of truth: the bubble below renders
                       //   liveFinalizeArgs?.analysis_markdown ?? preserved.text ?? liveStreamText
                       // so the dedup MUST compare the same string. Comparing against
                       // bare `liveStreamText` was empty under the structured-output
                       // contract (agent emits finalize, not text deltas), so the
                       // dedup never matched and the bubble stayed up next to the
                       // persisted message after a click-out/click-back.
                       const currentStreamingText =
                          liveFinalizeArgs?.analysis_markdown
                          || preservedStreamingContentRef.current?.text
                          || liveStreamText;
                       const currentStreamingToolCalls = currentToolCalls;
                       // Include liveThinkingList so a pure-thinking state (no tool calls yet)
                       // still satisfies the hasStreamingContent gate and the bubble renders.
                       const hasStreamingContent = !!(currentStreamingText || currentStreamingToolCalls.length > 0 || liveThinkingList.length > 0);
                       // Render the bubble the moment send fires (showAIThinking) so the
                       // "Starting…" indicator appears instantly — don't wait for the
                       // first chunk to land. hasStreamingContent still drives the rich
                       // states (thinking text, tool calls) once data arrives.
                       const shouldShowStreaming = isLoading || awaitingPersistAiRef.current || hasStreamingContent;

                       // Don't show streaming bubble if we already rendered a live or preserved virtual message
                       const hasLiveInMessages = selectedChat?.messages.some((m) => (m as any)._isLiveStreaming);
                       const hasPreservedInMessages = selectedChat?.messages.some((m) => (m as any)._isPreservedStreaming);
                       if (hasLiveInMessages || hasPreservedInMessages) {
                         // The live streaming content is already in mergedMessages, so don't show it again as a streaming bubble
                         return false;
                       }

                       if (DEBUG_STREAMING) console.log('[Render] Streaming bubble check:', {
                         isLoading,
                         awaitingPersistAiRef: awaitingPersistAiRef.current,
                         hasLiveStreamText: !!liveStreamText,
                         liveStreamTextLength: liveStreamText?.length || 0,
                         preservedTextLength: preservedStreamingContentRef.current?.text?.length || 0,
                         currentToolCallsCount: currentToolCalls.length,
                         hasStreamingContent,
                         shouldShowStreaming,
                       });
                       
                       if (!shouldShowStreaming) {
                         if (DEBUG_STREAMING) console.log('[Render] Not showing streaming bubble - conditions not met');
                         return false;
                       }
                       
                       const msgs: any[] = (selectedChatFromAPI as any)?.messages || [];
                       
                       // Check if a persisted message exists for the streaming messageOrder
                       // Check both current stream and preserved stream
                       const messageOrderToCheck = awaitingPersistMessageOrderRef.current ?? streamingMessageOrderRef.current;
                       if (messageOrderToCheck !== null) {
                         // Check both API messages and merged messages (in case deduplication already happened)
                         const persistedForStreamingOrder = msgs.find(
                           (msg: any) => msg?.messageOrder === messageOrderToCheck &&
                           (msg?.source === 'AI' || msg?.sender === 'AI')
                         ) || mergedMessages.find(
                           (msg: any) => msg?.messageOrder === messageOrderToCheck &&
                           msg?.sender === MessageSender.AI
                         );
                         
                         if (persistedForStreamingOrder) {
                           if (DEBUG_STREAMING) console.log('[Render] Not showing streaming bubble - message already persisted', {
                             messageOrder: messageOrderToCheck,
                             messageId: persistedForStreamingOrder.id,
                             foundIn: msgs.find((m: any) => m?.messageOrder === messageOrderToCheck) ? 'apiMsgs' : 'mergedMessages',
                           });
                           return false;
                         }
                       }
                       
                       let lastAiContent = '';
                       let lastAiTimestamp = 0;
                       for (let i = msgs.length - 1; i >= 0; i--) {
                         if (msgs[i]?.sender === 'AI' || msgs[i]?.source === 'AI') { 
                           lastAiContent = stripUiSpecFromContent(msgs[i]?.content || '');
                           // Check if this message was created recently (within last 5 seconds)
                           // to avoid showing duplicate if it just got persisted
                           const msgTime = new Date(msgs[i]?.createdAt || msgs[i]?.timestamp || 0).getTime();
                           lastAiTimestamp = msgTime;
                           break; 
                         }
                       }
                       
                       // Pre-content: send fired but the agent hasn't streamed yet.
                       // Surface the "Starting…" indicator immediately so the UI feels
                       // responsive in the gap before the first chunk arrives.
                       if (!currentStreamingText && (showAIThinking || liveThinkingList.length > 0 || currentStreamingToolCalls.length > 0)) {
                         if (DEBUG_STREAMING) console.log('[Render] Showing streaming bubble - pre-content or thinking/tool calls');
                         return true;
                       }
                       
                       const displayLiveText = stripUiSpecFromContent(currentStreamingText || '');
                       const norm = (s: string) => s.trim().replace(/\s+/g, ' ');
                       const normalizedLive = norm(displayLiveText);
                       const normalizedPersisted = norm(lastAiContent);
                       
                       // Check if we have raw content (before stripping) - important for UI-only messages
                       // Use actual length, not trimmed, to catch whitespace-only content
                       const rawLiveLength = (currentStreamingText || '').length;
                       const rawPersistedLength = (lastAiContent || '').trim().length;
                       
                       if (DEBUG_STREAMING) console.log('[Render] Streaming vs persisted comparison:', {
                         rawLiveLength,
                         rawPersistedLength,
                         normalizedLiveLength: normalizedLive.length,
                         normalizedPersistedLength: normalizedPersisted.length,
                         lengthDiff: Math.abs(normalizedLive.length - normalizedPersisted.length),
                         rawLengthDiff: Math.abs(rawLiveLength - rawPersistedLength),
                         timeSincePersisted: lastAiTimestamp > 0 ? Date.now() - lastAiTimestamp : 'N/A',
                         hasRawContent: rawLiveLength > 0,
                         hasNormalizedContent: normalizedLive.length > 0,
                       });
                       
                       // Don't show if content matches (allowing for small formatting differences)
                       // Check both normalized AND raw content to handle UI-only messages
                       const normalizedMatches = normalizedPersisted.length > 0 && 
                           (normalizedLive === normalizedPersisted || 
                            Math.abs(normalizedLive.length - normalizedPersisted.length) < 50);
                       
                       const rawMatches = rawPersistedLength > 0 && 
                           Math.abs(rawLiveLength - rawPersistedLength) < 200;
                       
                       if (normalizedMatches || rawMatches) {
                         // Also check if the persisted message is very recent (within 5 seconds)
                         // If so, assume it's the same message and hide streaming
                         const timeSincePersisted = Date.now() - lastAiTimestamp;
                         if (timeSincePersisted < 5000) {
                           if (DEBUG_STREAMING) console.log('[Render] Hiding streaming bubble - matches persisted message (recent)', {
                             normalizedMatches,
                             rawMatches,
                             timeSincePersisted,
                           });
                           return false;
                         }
                       }
                       
                       // Show if we have ANY content (raw OR normalized OR thinking/tool calls)
                       // OR a send just fired (showAIThinking) — the pre-content "Starting…"
                       // pill should stay visible until real content arrives.
                       const willShow = rawLiveLength > 0 || normalizedLive.length > 0 || currentStreamingToolCalls.length > 0 || liveThinkingList.length > 0 || showAIThinking;
                       if (DEBUG_STREAMING) console.log('[Render] Streaming bubble decision:', {
                         willShow,
                         rawLiveLength,
                         normalizedLiveLength: normalizedLive.length,
                         hasThinking: liveThinkingList.length > 0,
                         hasToolCalls: currentStreamingToolCalls.length > 0,
                         reason: !willShow ? 'no content' : 
                                 (normalizedMatches || rawMatches) ? 'matches persisted' : 'showing',
                       });
                       return willShow;
                     })() ) && (
                   <div>
                     {/* Show thinking/tool calls FIRST (matches persisted message order) */}
                    {(showAIThinking ||
                      ((preservedStreamingContentRef.current as any)?.thinkingList?.length ?? 0) > 0 ||
                      (liveThinkingList.length > 0) ||
                       (preservedStreamingContentRef.current?.toolCalls?.length ?? 0) > 0 || currentToolCalls.length > 0) && (
                       <AIProgressIndicator
                         state={aiProgressState}
                         statusText={aiProgressStatus}
                         thinkingItems={(preservedStreamingContentRef.current as any)?.thinkingList ?? liveThinkingList}
                         activeTools={(preservedStreamingContentRef.current?.toolCalls || currentToolCalls).map((tc: any) => ({
                           name: tc.name,
                           isActive: activeToolCalls.has(tc.name)
                         }))}
                       />
                     )}
                     {/* Then show the actual response text */}
                     {(() => {
                       const streamingText = preservedStreamingContentRef.current?.text || liveStreamText;
                       // Prefer the live finalize envelope when present.
                       const spec: UIChoiceSpec | null =
                         liveFinalizeArgs?.kind === 'clarifying_question' && liveFinalizeArgs.question
                           ? { type: 'choice', ...liveFinalizeArgs.question }
                           : tryParseGenerativeUISpec(streamingText);
                       const displayLiveText =
                         liveFinalizeArgs?.analysis_markdown
                         ?? stripUiSpecFromContent(streamingText);

                       const hasDisplayContent = displayLiveText.trim().length > 0;

                       if (DEBUG_STREAMING) console.log('[Render] Streaming bubble content check:', {
                         rawLiveStreamLength: streamingText?.length || 0,
                         displayLiveTextLength: displayLiveText.length,
                         hasDisplayContent,
                         hasUISpec: !!spec,
                         isPreserved: !!preservedStreamingContentRef.current,
                         hasFinalizeArgs: !!liveFinalizeArgs,
                       });

                       return (
                         <>
                           {hasDisplayContent && (
                             <ChatBubble message={displayLiveText} sender={MessageSender.AI} />
                           )}
                           {spec && (
                             <GenerativeUIBlock spec={spec} onSubmit={handleGenerativeUISubmit} />
                           )}
                         </>
                       );
                     })()}
                   </div>
                 )}
                {/* Removed: the redundant "unified progress indicator" that used to live
                    here was a second AIProgressIndicator that fired whenever
                    isLoading && !liveStreamText && !_isLiveStreaming-in-messages.
                    The bottom streaming bubble (the IIFE above) already covers every
                    case this handled, and the two rendered simultaneously when
                    streamingMessageOrderRef was null (no virtual yet) but
                    currentToolCalls was non-empty — producing the double
                    "Reasoning..." card bug. The bottom bubble now also checks
                    liveThinkingList.length so the pure-thinking state is covered. */}
              </div>
            </div>
            </ChatErrorBoundary>
          ) : (
            <NewChatView
              userName={user?.firstName || "there"}
              onExampleClick={handleExampleMessageClick}
            />
          )}
          <Box
            ref={bottomOverlayRef}
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              background: "transparent",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <ChatInputArea
              value={currentMessage}
              onChange={setCurrentMessage}
              onSend={handleSendMessage}
              onKeyPress={handleKeyPress}
              isLoading={isLoading}
              // Attachment props
              pendingAttachments={pendingAttachments}
              onFileSelect={handleFileSelect}
              onRemoveAttachment={handleRemoveAttachment}
              isUploading={isUploadingAttachment}
              chatId={selectedChatId}
            />
          </Box>
        </div>
      </div>
    </div>
  );
};

export default ChatAdvisor;
